import React, { useState, useEffect, useCallback, useRef } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import * as api from "./api";
import "./App.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

const COLORS = ["#1a2332", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

// Fragezeichen-Tooltip: hover zeigt Erklärungstext
// Bubble erscheint oben ODER unten je nach Position auf der Seite
function HelpTip({ text }) {
  const [show, setShow] = useState(false);
  const [above, setAbove] = useState(true);
  const ref = useRef(null);
  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setShow(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleShow = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      // Wenn weniger als 160px Platz nach oben → Bubble nach unten
      setAbove(rect.top > 160);
    }
    setShow(v => !v);
  };

  return (
    <span className="helptip" ref={ref} onMouseEnter={() => { if (ref.current) { const r = ref.current.getBoundingClientRect(); setAbove(r.top > 160); } setShow(true); }} onMouseLeave={() => setShow(false)} onClick={handleShow}>
      <span className="helptip-icon">?</span>
      {show && <span className={`helptip-bubble ${above ? "" : "helptip-bubble-below"}`}>{text}</span>}
    </span>
  );
}

// Ablauf der App in zwei Schritten:
// 1. SEARCH    — Suchleiste (KI oder einfach) liefert eine Liste von Statistik-Vorschlägen
// 2. RESULTS   — Chart + Wide-Tabelle direkt nach Auswahl, mit Filterleiste oben
//                (Filter sind spezifisch für die gewählte Statistik — nur was
//                wirklich vorkommt, z.B. kein Gemeinde-Filter bei rein kantonalen Daten)
// Schweizer Zahlenformat: Tausendertrennung mit Apostroph, Dezimalpunkt.
// 1000 -> 1'000   1234.56 -> 1'234.56
function formatNumber(value, maxDecimals = 2) {
  if (value === null || value === undefined || value === "") return "";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (!isFinite(n)) return String(value);
  return n.toLocaleString("de-CH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).replace(/\u2019/g, "'").replace(/\u202f|\u00a0/g, "'");
}

const STEP = { SEARCH: "search", RESULTS: "results", OVERVIEW: "overview", ADMIN: "admin", CHAT: "chat", IMPORT: "import" };

// Hilfsfunktion: Werte aus einem <select multiple> auslesen
function getSelectedValues(selectEl) {
  return Array.from(selectEl.selectedOptions).map((o) => o.value);
}

// Durchsuchbares Einzelauswahl-Dropdown für die Advanced Search: Klick öffnet
// ein Panel mit einem Suchfeld oben, das die Optionsliste live filtert —
// nützlich bei langen Listen (z.B. alle Datensätze über beide Quellen),
// wo man nicht weiss, wie der Eintrag exakt benannt ist.
function SearchableSelect({ label, value, options, onChange, placeholder = "Alle" }) {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setFilterText("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);
  const summary = selectedOption ? selectedOption.label : placeholder;

  const filtered = filterText.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(filterText.trim().toLowerCase()))
    : options;

  const choose = (val) => {
    onChange(val);
    setOpen(false);
    setFilterText("");
  };

  return (
    <div className="checkbox-filter field" ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        className={`checkbox-filter-trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{summary}</span>
        <span className="checkbox-filter-arrow">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="checkbox-filter-panel">
          <input
            type="text"
            className="searchable-select-input"
            placeholder="Suchen…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            autoFocus
          />
          <div
            className={`checkbox-filter-item searchable-select-option ${!value ? "is-selected" : ""}`}
            onClick={() => choose("")}
          >
            {placeholder}
          </div>
          {filtered.map((o) => (
            <div
              key={o.value}
              className={`checkbox-filter-item searchable-select-option ${value === o.value ? "is-selected" : ""}`}
              onClick={() => choose(o.value)}
            >
              {o.label}
            </div>
          ))}
          {filtered.length === 0 && <div className="searchable-select-empty">Keine Treffer</div>}
        </div>
      )}
    </div>
  );
}
// "Alle auswählen" + einer Checkbox pro Option. Zeigt im geschlossenen
// Zustand "Alle" oder "X ausgewählt" an.
//
// WICHTIG: "selected" ist ein leeres Array sowohl im Ausgangszustand
// ("Alle", noch nichts manuell verändert) als auch wenn der Nutzer aktiv
// ALLES abgewählt hat. Damit "Alle" anklicken danach wieder alles auswählt
// (statt wirkungslos zu bleiben, weil beide Zustände gleich aussehen),
// unterscheiden wir intern per allMode zwischen diesen beiden Fällen.
function CheckboxFilterDropdown({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isAllMode = selected.length === 0;
  const isNoneMode = selected.length === 1 && selected[0] === "__NONE__";
  const effectiveSelected = isNoneMode ? [] : selected;
  const allChecked = isAllMode;

  const summary = isNoneMode
    ? "Keine"
    : isAllMode
    ? "Alle"
    : `${effectiveSelected.length} ausgewählt`;

  const filteredOptions = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  const toggleAll = () => {
    onChange(allChecked ? ["__NONE__"] : []);
  };

  const toggleOne = (value) => {
    const base = isNoneMode ? [] : (isAllMode ? options.map((o) => o.value) : effectiveSelected);
    const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
    if (next.length === 0) {
      onChange(["__NONE__"]);
    } else if (next.length === options.length) {
      onChange([]);
    } else {
      onChange(next);
    }
  };

  return (
    <div className="checkbox-filter field" ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        className={`checkbox-filter-trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{summary}</span>
        <span className="checkbox-filter-arrow">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="checkbox-filter-panel">
          {options.length > 6 && (
            <input
              type="text"
              className="searchable-select-input"
              placeholder="Suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          )}
          <label className="checkbox-filter-item checkbox-filter-all">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
            <span>Alle</span>
          </label>
          <div className="checkbox-filter-divider" />
          {filteredOptions.map((o) => (
            <label key={o.value} className="checkbox-filter-item">
              <input
                type="checkbox"
                checked={isAllMode || effectiveSelected.includes(o.value)}
                onChange={() => toggleOne(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
          {filteredOptions.length === 0 && (
            <div className="searchable-select-empty">Keine Treffer</div>
          )}
        </div>
      )}
    </div>
  );
}

// Levenshtein-Distanz: misst wie viele Zeichen-Operationen (einfügen, löschen,
// ersetzen) nötig sind, um string a in b umzuwandeln. Distanz 0 = identisch,
// 1 = ein Tippfehler (z.B. "Abeit" → "Arbeit"). Wird genutzt, um
// Suchvorschläge auch bei kleinen Tippfehlern zu zeigen.
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => Array.from({ length: n + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

// Gibt true zurück wenn der Suchbegriff q "fuzzy" auf den Text passt:
// entweder als Substring (normal) ODER wenn ein einzelnes Wort im Text
// eine kleine Levenshtein-Distanz zum Suchbegriff hat (Tippfehler-Toleranz).
function fuzzyMatch(text, q) {
  if (!text || !q) return false;
  const tl = text.toLowerCase();
  const ql = q.toLowerCase();
  if (tl.includes(ql)) return true;
  // Tippfehler: maximal 1 Fehler bei kurzen Begriffen (≤5 Zeichen), 2 bei längeren
  const maxDist = ql.length <= 5 ? 1 : 2;
  const words = tl.split(/[\s\-\/&]+/);
  return words.some((word) => {
    if (Math.abs(word.length - ql.length) > maxDist) return false;
    return levenshtein(word, ql) <= maxDist;
  });
}

// ---------- Daten-Transformationsmodi ----------
// Alle Funktionen arbeiten auf dem results-Objekt {years, groups, ...} und
// geben ein neues Objekt mit transformierten Werten zurück. Die Rohdaten
// vom Backend werden NICHT verändert — der Modus gilt nur für Anzeige/Export.

const MODES = [
  { id: "original",  label: "Original" },
  { id: "index",     label: "Indexiert" },
  { id: "rel_yoy",   label: "Δ% YoY" },
  { id: "abs_yoy",   label: "Δ abs. YoY" },
];

function applyMode(results, mode, baseYear) {
  if (!results || mode === "original") return results;

  const { years, groups } = results;
  const sortedYears = [...years].sort();

  const newGroups = groups.map((g) => {
    const newValues = {};

    if (mode === "index") {
      // Indexierung: Basisjahr = 100, alle anderen relativ dazu
      const baseKey = Object.keys(g.values).find(k => String(k) === String(baseYear));
      const base = baseKey != null ? g.values[baseKey] : null;
      if (base == null || base === 0) return { ...g, values: newValues };
      for (const y of sortedYears) {
        const v = g.values[y];
        newValues[y] = v != null ? Math.round((v / base) * 100 * 100) / 100 : null;
      }
    } else if (mode === "rel_yoy") {
      // Relative Veränderung zum Vorjahr in %
      for (let i = 0; i < sortedYears.length; i++) {
        const y = sortedYears[i];
        const prev = sortedYears[i - 1];
        const v = g.values[y];
        const vPrev = prev != null ? g.values[prev] : null;
        if (i === 0 || vPrev == null || vPrev === 0) {
          newValues[y] = null;
        } else {
          newValues[y] = Math.round(((v - vPrev) / Math.abs(vPrev)) * 100 * 100) / 100;
        }
      }
    } else if (mode === "abs_yoy") {
      // Absolute Veränderung zum Vorjahr
      for (let i = 0; i < sortedYears.length; i++) {
        const y = sortedYears[i];
        const prev = sortedYears[i - 1];
        const v = g.values[y];
        const vPrev = prev != null ? g.values[prev] : null;
        if (i === 0 || vPrev == null || v == null) {
          newValues[y] = null;
        } else {
          newValues[y] = Math.round((v - vPrev) * 100) / 100;
        }
      }
    }

    return { ...g, values: newValues };
  });

  return { ...results, groups: newGroups };
}

// ---------- Chat Page ----------
// Inline-Statistik-Karte mit Mini-Chart im Chat
function InlineStat({ stat, statInfo, onOpenStat }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stat) return;
    const load = async () => {
      try {
        let result;
        if (stat.source === "bfs") {
          result = await api.bfsQuery(stat.bfs_table_id, undefined, undefined, null, null);
        } else {
          result = await api.search({
            themenfeld: stat.themenfeld,
            indikator: stat.indikator,
            datensatz: stat.datensatz,
          });
        }
        setData(result);
      } catch {}
      setLoading(false);
    };
    load();
  }, [stat]);

  const title = statInfo?.title || stat.bfs_title || `${stat.indikator} — ${stat.datensatz}`;

  return (
    <div className="chat-inline-stat">
      <div className="chat-inline-stat-header">
        <span className={`source-badge ${stat.source}`}>{stat.source === "bfs" ? "BFS" : "Eigene"}</span>
        <span className="chat-inline-stat-title">{title}</span>
        <button className="chatbot-open-btn" onClick={() => onOpenStat?.(stat)}>📊 Vollbild</button>
      </div>
      {loading && <div className="chat-inline-loading">Lade Daten…</div>}
      {!loading && data && data.years && data.groups && data.groups.length > 0 && (
        <div className="chat-inline-chart">
          <Line
            data={{
              labels: data.years,
              datasets: data.groups.slice(0, 5).map((g, i) => ({
                label: g.Gemeinde || g.Datensatz || "",
                data: data.years.map(y => g.values[y] ?? null),
                borderColor: COLORS[i % COLORS.length],
                backgroundColor: COLORS[i % COLORS.length] + "20",
                borderWidth: 2,
                pointRadius: 2,
                tension: 0.3,
                spanGaps: true,
              })),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              animation: false,
              plugins: {
                legend: { display: data.groups.length > 1, position: "bottom", labels: { font: { size: 10 }, boxWidth: 12 } },
                tooltip: {
                  mode: "nearest", intersect: true,
                  callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y)}` },
                },
              },
              scales: {
                y: { grid: { color: "#f3f4f6" }, ticks: { font: { size: 10 }, callback: (v) => formatNumber(v) } },
                x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 8 } },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

// Einfacher Markdown-Renderer für Chat-Nachrichten
function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^- (.+)$/gm, "• $1")
    .replace(/\n/g, "<br/>");
}

function ChatPage({ currentUser, onOpenStat, pendingQuestion, onPendingConsumed, pendingChatId }) {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadChats().then(() => {
      if (pendingChatId) {
        onPendingConsumed?.();
        openChat(pendingChatId);
      } else if (pendingQuestion) {
        onPendingConsumed?.();
        autoStartChat(pendingQuestion);
      }
    });
  }, []);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [activeChat?.messages]);

  const loadChats = async () => {
    setLoadingChats(true);
    try { const list = await api.getChats(); setChats(list); } catch {}
    setLoadingChats(false);
  };

  const autoStartChat = async (question) => {
    const c = await api.createChat();
    setChats(prev => [{ id: c.id, title: question.slice(0,60), message_count: 0, created_at: c.created_at }, ...prev]);
    setActiveChatId(c.id);
    setActiveChat(c);
    await sendQuestion(c.id, question);
  };

  const openChat = async (chatId) => {
    setActiveChatId(chatId);
    try { const c = await api.getChat(chatId); setActiveChat(c); } catch {}
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const newChat = async () => {
    const c = await api.createChat();
    setChats(prev => [{ id: c.id, title: c.title, message_count: 0, created_at: c.created_at }, ...prev]);
    setActiveChatId(c.id); setActiveChat(c);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const removeChat = async (chatId, e) => {
    e.stopPropagation();
    await api.deleteChat(chatId);
    setChats(prev => prev.filter(c => c.id !== chatId));
    if (activeChatId === chatId) { setActiveChatId(null); setActiveChat(null); }
  };

  const startRename = (chat, e) => { e.stopPropagation(); setRenamingId(chat.id); setRenameVal(chat.title); };

  const saveRename = async (chatId) => {
    if (renameVal.trim()) {
      await api.renameChat(chatId, renameVal.trim());
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, title: renameVal.trim() } : c));
      if (activeChat?.id === chatId) setActiveChat(prev => ({ ...prev, title: renameVal.trim() }));
    }
    setRenamingId(null);
  };

  const sendQuestion = async (chatId, question) => {
    setLoading(true);
    setActiveChat(prev => ({ ...prev, messages: [...(prev?.messages || []), { role: "user", content: question, ts: new Date().toISOString() }] }));
    try {
      const res = await api.sendChatMessage(chatId, question);
      setActiveChat(prev => ({ ...prev, messages: [...(prev?.messages || []), { role: "assistant", content: res.answer, ts: new Date().toISOString(), stat: res.stat, stat_info: res.stat_info }] }));
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, title: question.slice(0,60), message_count: (c.message_count||0)+2 } : c));
    } catch (e) {
      setActiveChat(prev => ({ ...prev, messages: [...(prev?.messages||[]), { role: "assistant", content: "Fehler: "+e.message, ts: new Date().toISOString() }] }));
    }
    setLoading(false);
  };

  const send = async () => {
    if (!input.trim() || loading || !activeChatId) return;
    const q = input.trim(); setInput("");
    await sendQuestion(activeChatId, q);
  };

  return (
    <div className="chat-page">
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <span className="chat-sidebar-title">Meine Chats</span>
          <button className="chat-new-btn" onClick={newChat}>+</button>
        </div>
        {loadingChats ? <div className="chat-sidebar-empty">Lädt…</div> :
         chats.length === 0 ? <div className="chat-sidebar-empty">Noch keine Chats.<br/>Klick + um zu starten.</div> : (
          <div className="chat-list">
            {chats.map(c => (
              <div key={c.id} className={`chat-list-item ${activeChatId===c.id?"active":""}`} onClick={() => openChat(c.id)}>
                {renamingId===c.id ? (
                  <input className="chat-rename-input" value={renameVal} onChange={e=>setRenameVal(e.target.value)}
                    onBlur={()=>saveRename(c.id)} onKeyDown={e=>e.key==="Enter"&&saveRename(c.id)} autoFocus onClick={e=>e.stopPropagation()} />
                ) : (<>
                  <span className="chat-list-title">{c.title}</span>
                  <div className="chat-list-actions">
                    <button onClick={e=>startRename(c,e)}>✎</button>
                    <button onClick={e=>removeChat(c.id,e)}>✕</button>
                  </div>
                </>)}
              </div>
            ))}
          </div>
        )}
        <div className="chat-sidebar-footer">
          <div className="chat-sidebar-user">👤 {currentUser?.name||currentUser?.email}</div>
          <div className="chat-sidebar-hint">Chats werden nach 30 Tagen automatisch gelöscht</div>
        </div>
      </div>

      <div className="chat-main">
        {!activeChatId ? (
          <div className="chat-welcome">
            <div className="chat-welcome-icon">🤖</div>
            <h2>Daten-Assistent</h2>
            <p>Stell mir Fragen zu Wirtschaftsdaten des Kantons Schaffhausen.<br/>Z.B. «Wie viele Arbeitslosen gab es 2014?»</p>
            <button className="btn-primary" onClick={newChat}>Neuen Chat starten</button>
          </div>
        ) : (<>
          <div className="chat-messages-area">
            {(activeChat?.messages||[]).map((m,i) => (
              <div key={i} className={`chat-full-msg ${m.role}`}>
                <div className="chat-full-avatar">{m.role==="user"?"👤":"🤖"}</div>
                <div className="chat-full-content">
                  <div className="chat-full-bubble" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                  {m.role==="assistant" && m.stat && (
                    <InlineStat stat={m.stat} statInfo={m.stat_info} onOpenStat={(stat) => onOpenStat?.(stat, activeChatId)} />
                  )}
                  <div className="chat-full-ts">{m.ts?new Date(m.ts).toLocaleString("de-CH"):""}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="chat-full-msg assistant">
                <div className="chat-full-avatar">🤖</div>
                <div className="chat-full-content"><div className="chatbot-bubble chatbot-typing"><span/><span/><span/></div></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="chat-input-area">
            <input ref={inputRef} className="chat-full-input" placeholder="Frage stellen… (Enter)" value={input}
              onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()} disabled={loading} />
            <button className="chat-full-send" onClick={send} disabled={loading||!input.trim()}>↑</button>
          </div>
        </>)}
      </div>
    </div>
  );
}


// ---------- Imports Log Tab ----------
function ImportsTab({ pw, msg, setMsg }) {
  const [imports, setImports] = useState([]);
  const [undoing, setUndoing] = useState(null);

  useEffect(() => { loadImports(); }, []);

  const loadImports = async () => {
    try {
      const d = await api.adminGetImports(pw);
      setImports(d.imports || []);
    } catch (e) {
      setMsg("Import-Log konnte nicht geladen werden: " + e.message);
    }
  };

  const undo = async (imp) => {
    if (!window.confirm(`Wirklich alle ${imp.inserted} Zeilen von «${imp.datensatz}» löschen?`)) return;
    setUndoing(imp.id);
    try {
      const r = await api.adminUndoImport(pw, imp.id);
      setMsg(`Import rückgängig: ${r.deleted} Zeilen von «${r.datensatz}» gelöscht.`);
      loadImports();
    } catch (e) { setMsg("Fehler: " + e.message); }
    setUndoing(null);
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3>Import-Log <span className="admin-count">{imports.length} Einträge</span></h3>
        <button className="btn-secondary" onClick={loadImports}>↻ Aktualisieren</button>
      </div>
      <div className="admin-log-table">
        <table>
          <thead><tr><th>Zeit</th><th>User</th><th>Datensatz</th><th>Quelle</th><th>Zeilen</th><th>Modus</th><th></th></tr></thead>
          <tbody>
            {imports.length === 0 && <tr><td colSpan={7} style={{color:"var(--gray-400)",padding:12}}>Noch keine Imports</td></tr>}
            {imports.map(imp => (
              <tr key={imp.id}>
                <td className="admin-log-ts">{imp.ts ? new Date(imp.ts).toLocaleString("de-CH") : "—"}</td>
                <td>{imp.user}</td>
                <td>
                  <div style={{fontWeight:600,fontSize:"0.83rem"}}>{imp.datensatz}</div>
                  <div style={{fontSize:"0.75rem",color:"var(--gray-400)"}}>{imp.themenfeld} → {imp.indikator}</div>
                </td>
                <td style={{fontSize:"0.78rem",color:"var(--gray-700)",maxWidth:160,wordBreak:"break-word"}}>{imp.quelle || "—"}</td>
                <td>
                  <span style={{color:"#10b981",fontWeight:700}}>+{imp.inserted}</span>
                  {imp.deleted > 0 && <span style={{color:"#ef4444",marginLeft:6}}>−{imp.deleted}</span>}
                  {imp.skipped > 0 && <span style={{color:"var(--gray-400)",marginLeft:6,fontSize:"0.8rem"}}>({imp.skipped} skip)</span>}
                </td>
                <td><span className="admin-log-badge">{imp.mode === "replace" ? "Ersetzen" : "Hinzufügen"}</span></td>
                <td>
                  <button
                    className="btn-secondary"
                    style={{fontSize:"0.76rem",padding:"4px 10px",color:"#ef4444",borderColor:"rgba(239,68,68,0.3)"}}
                    onClick={() => undo(imp)}
                    disabled={undoing === imp.id}
                  >
                    {undoing === imp.id ? "..." : "↩ Rückgängig"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{fontSize:"0.74rem",color:"var(--gray-400)",marginTop:8}}>
        "Rückgängig" löscht ALLE Zeilen des Datensatzes in Baserow — nicht nur die importierten.
      </p>
    </div>
  );
}

// ---------- Rollen-Verwaltung Tab ----------
function RolesTab({ pw, msg, setMsg }) {
  const [roles, setRoles] = useState([]);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      setRoles(await api.adminGetRoles(pw));
    } catch (e) {
      setMsg("Rollen konnten nicht geladen werden: " + e.message);
    }
  };

  const changeRole = async (user, role) => {
    try {
      await api.adminSetRole(pw, user.oid, user.email, user.name, role || null);
      setMsg(`Rolle für ${user.name || user.email} ${role ? `auf "${role}" gesetzt` : "entfernt"}.`);
      loadRoles();
    } catch (e) { setMsg("Fehler: " + e.message); }
  };

  return (
    <div className="admin-section">
      <h3>Rollen-Verwaltung</h3>
      <p style={{fontSize:"0.83rem",color:"var(--gray-700)",marginBottom:16}}>
        Alle User die sich je eingeloggt haben. <b>Admin</b> = alles. <b>Editor</b> = nur Daten-Import. <b>—</b> = normaler Lesezugriff.
      </p>
      <table className="admin-token-table">
        <thead>
          <tr><th>Name</th><th>E-Mail</th><th>Rolle</th></tr>
        </thead>
        <tbody>
          {roles.length === 0 && (
            <tr><td colSpan={3} style={{color:"var(--gray-400)",padding:12}}>Noch keine Logins aufgezeichnet</td></tr>
          )}
          {roles.map(r => (
            <tr key={r.oid}>
              <td style={{fontWeight:600}}>{r.name || "—"}</td>
              <td style={{color:"var(--gray-700)"}}>{r.email}</td>
              <td>
                <select
                  value={r.role || ""}
                  onChange={e => changeRole(r, e.target.value)}
                  style={{padding:"5px 10px",borderRadius:8,border:"1px solid rgba(17,17,17,0.12)",fontFamily:"inherit",fontSize:"0.84rem",background:"white"}}
                >
                  <option value="">— Kein (Lesezugriff)</option>
                  <option value="editor">Editor (Import)</option>
                  <option value="admin">Admin (alles)</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{fontSize:"0.74rem",color:"var(--gray-400)",marginTop:12}}>
        Neue User erscheinen hier automatisch nach ihrem ersten Login. Die OID (Azure Object-ID) wird automatisch erkannt.
      </p>
    </div>
  );
}

// ---------- Import Page ----------
function ImportPage({ myRole, themenfelder, allDatensaetze }) {
  const [importType, setImportType] = useState("csv"); // "csv" | "bfs"

  return (
    <div className="import-page">
      <div className="import-header">
        <h2>Daten importieren</h2>
        <div className="import-type-tabs">
          <button className={`import-type-tab ${importType==="csv"?"active":""}`} onClick={()=>setImportType("csv")}>📄 CSV / Eigene Daten</button>
          <button className={`import-type-tab ${importType==="bfs"?"active":""}`} onClick={()=>setImportType("bfs")}>🏛 BFS STAT-TAB</button>
          <button className={`import-type-tab ${importType==="sdmx"?"active":""}`} onClick={()=>setImportType("sdmx")}>🌐 stats.swiss</button>
        </div>
      </div>
      {importType === "csv" ? <CsvImport themenfelder={themenfelder} />
        : importType === "bfs" ? <BfsImport themenfelder={themenfelder} allDatensaetze={allDatensaetze} />
        : <SdmxImport themenfelder={themenfelder} />}
    </div>
  );
}

function BfsImport({ themenfelder, allDatensaetze }) {
  const [tableId, setTableId] = useState("");
  const [meta, setMeta] = useState({ themenfeld:"", indikator:"", datensatz:"", quelle:"" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("input"); // input | preview | result
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("append");

  const extractTableId = (input) => {
    const s = input.trim();
    // Direkte ID: px-x-XXXXXXXX_XXX
    if (/^px-x-\w+$/.test(s)) return s;
    // Aus URL extrahieren: suche px-x-XXXXXXXX_XXX Muster
    const match = s.match(/px-x-[\w]+_\d+/);
    return match ? match[0] : s;
  };

  const loadPreview = async () => {
    if (!tableId.trim()) { setError("Bitte px-Nummer eingeben"); return; }
    const id = extractTableId(tableId);

    // Prüfen ob diese Tabelle bereits in der App existiert
    const existing = allDatensaetze?.find(d => d.source === "bfs" && d.table_id === id);
    if (existing) {
      setError(`Diese Tabelle existiert bereits in der App als «${existing.indikator} — ${existing.datensatz}». Import nicht möglich — bei Bedarf zuerst im Admin-Portal unter 📥 Imports rückgängig machen.`);
      return;
    }
    setLoading(true); setError("");
    try {
      // BFS Metadaten laden für Vorschau
      const data = await api.bfsQuery(id, undefined, undefined, null, null, null, null);
      setPreview({ ...data, table_id: id });
      // Metadaten aus Titel vorausfüllen
      const title = data.groups?.[0]?.Datensatz || id;
      setMeta(m => ({
        ...m,
        datensatz: m.datensatz || title,
        quelle: m.quelle || `Bundesamt für Statistik (BFS) + www.bfs.admin.ch`,
      }));
      setStep("preview");
    } catch (e) { setError("Fehler: " + e.message); }
    setLoading(false);
  };

  const runImport = async () => {
    if (!confirmed) return;
    if (!meta.themenfeld.trim()) { setError("Pflichtfeld: Themenfeld"); return; }
    if (!meta.indikator.trim())  { setError("Pflichtfeld: Indikator"); return; }
    if (!meta.datensatz.trim())  { setError("Pflichtfeld: Datensatz-Name"); return; }
    if (!meta.quelle.trim())     { setError("Pflichtfeld: Quelle"); return; }
    setLoading(true); setError("");

    // BFS-Daten in Baserow-Format konvertieren und importieren
    const rows = [];
    if (preview?.groups && preview?.years) {
      for (const g of preview.groups) {
        for (const year of preview.years) {
          const val = g.values?.[year];
          if (val !== null && val !== undefined) {
            rows.push({
              Themenfeld: meta.themenfeld,
              Indikator: meta.indikator,
              Datensatz: meta.datensatz,
              Kanton: g.Gemeinde || "",
              "Filter 1": "",
              "Filteroptionen 1": "",
              Jahr: year,
              Wert: val,
            });
          }
        }
      }
    }

    try {
      const r = await api.importRun({
        password: "unused",
        themenfeld: meta.themenfeld,
        indikator: meta.indikator,
        datensatz: meta.datensatz,
        quelle: meta.quelle,
        mode, rows,
      });
      setResult(r); setStep("result");
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const reset = () => { setStep("input"); setTableId(""); setPreview(null); setResult(null); setConfirmed(false); setError(""); setMeta({ themenfeld:"", indikator:"", datensatz:"", quelle:"" }); };

  const STEPS = ["input","preview","result"];

  return (
    <div>
      <div className="import-stepper">
        {[["input","1. Tabelle wählen"],["preview","2. Metadaten & Kontrolle"],["result","3. Ergebnis"]].map(([s,label])=>(
          <div key={s} className={`import-step ${step===s?"active":""} ${STEPS.indexOf(step)>STEPS.indexOf(s)?"done":""}`}>{label}</div>
        ))}
      </div>

      {error && <div className="admin-msg" style={{borderColor:"#ef4444",background:"rgba(239,68,68,0.08)",marginBottom:16}}>{error}</div>}

      {step === "input" && (
        <div className="import-card">
          <h3>BFS STAT-TAB Tabelle importieren</h3>
          <p style={{fontSize:"0.84rem",color:"var(--gray-700)",marginBottom:20}}>
            Gib die px-Tabellen-ID ein — z.B. <code style={{background:"rgba(17,17,17,0.06)",padding:"2px 6px",borderRadius:4}}>px-x-0602010000_101</code> oder die vollständige URL der BFS-Tabelle.
          </p>
          <div className="field" style={{marginBottom:16}}>
            <label>px-Tabellen-ID oder URL * <HelpTip text="Die px-Nummer findest du in der URL der BFS STAT-TAB Seite, z.B. px-x-0602010000_101. Du kannst die ganze URL oder nur die ID eingeben." /></label>
            <input
              value={tableId}
              onChange={e => { setTableId(e.target.value); setError(""); }}
              placeholder="px-x-0602010000_101 oder https://www.pxweb.bfs.admin.ch/pxweb/de/px-x-..."
              onKeyDown={e => e.key === "Enter" && loadPreview()}
              style={{width:"100%"}}
            />
            {(() => {
              const id = extractTableId(tableId);
              const existing = id && allDatensaetze?.find(d => d.source === "bfs" && d.table_id === id);
              return existing ? (
                <div style={{marginTop:6,fontSize:"0.78rem",color:"#ef4444",background:"rgba(239,68,68,0.08)",padding:"6px 10px",borderRadius:6,border:"1px solid rgba(239,68,68,0.2)"}}>
                  ⚠ Diese Tabelle existiert bereits als «{existing.indikator} — {existing.datensatz}»
                </div>
              ) : null;
            })()}
          </div>
          <p style={{fontSize:"0.78rem",color:"var(--gray-400)",marginBottom:16}}>
            Die ID findest du in der URL der BFS STAT-TAB Seite. Beispiele bereits in der App verfügbare Tabellen: <em>px-x-0602010000_101</em> (Beschäftigte nach Kanton), <em>px-x-0102010000_101</em> (Wohnbevölkerung nach Gemeinde).
          </p>
          <div className="import-actions" style={{marginTop:0}}>
            <button className="btn-primary" onClick={loadPreview} disabled={loading||!tableId.trim()}>
              {loading ? "Lade Vorschau…" : "Vorschau laden →"}
            </button>
          </div>
        </div>
      )}

      {step === "preview" && preview && (
        <div className="import-card">
          <h3>Metadaten &amp; Kontrolle</h3>

          <div className="import-summary-grid" style={{marginBottom:20}}>
            <div className="import-summary-item">
              <div className="import-summary-label">Tabellen-ID</div>
              <div className="import-summary-val" style={{fontSize:"0.8rem",fontFamily:"monospace"}}>{preview.table_id}</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Datenpunkte</div>
              <div className="import-summary-val">{formatNumber(preview.raw_count || (preview.years?.length * preview.groups?.length) || 0, 0) || "?"}</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Jahre</div>
              <div className="import-summary-val">{preview.years?.[0]}–{preview.years?.[preview.years.length-1]} ({preview.years?.length})</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Gruppen / Linien</div>
              <div className="import-summary-val">{preview.groups?.length}</div>
            </div>
          </div>

          <div className="import-fields" style={{marginBottom:16}}>
            <div className="field">
              <label>Themenfeld * <HelpTip text="Oberste Kategorie in der App, z.B. 'Arbeit', 'Bevölkerung', 'Unternehmen'" /></label>
              <input list="tf-bfs-list" value={meta.themenfeld} onChange={e=>setMeta(m=>({...m,themenfeld:e.target.value}))} placeholder="z.B. Arbeit" />
              <datalist id="tf-bfs-list">{themenfelder.map(t=><option key={t} value={t}/>)}</datalist>
            </div>
            <div className="field">
              <label>Indikator * <HelpTip text="Fachlicher Begriff, z.B. 'Beschäftigung', 'Arbeitslosigkeit'" /></label>
              <input value={meta.indikator} onChange={e=>setMeta(m=>({...m,indikator:e.target.value}))} placeholder="z.B. Beschäftigung" />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Datensatz-Name * <HelpTip text="So erscheint die Statistik in der App-Suche" /></label>
              <input value={meta.datensatz} onChange={e=>setMeta(m=>({...m,datensatz:e.target.value}))} placeholder="z.B. Beschäftigte nach Kanton und Sektor" />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Quelle *</label>
              <input value={meta.quelle} onChange={e=>setMeta(m=>({...m,quelle:e.target.value}))} placeholder="z.B. Bundesamt für Statistik (BFS) + www.bfs.admin.ch" />
            </div>
          </div>

          <div className="import-mode" style={{marginBottom:16}}>
            <label><b>Import-Modus:</b> <HelpTip text="Hinzufügen: bestehende Daten bleiben. Ersetzen: bestehende Zeilen werden zuerst gelöscht." /></label>
            <label className="import-radio"><input type="radio" value="append" checked={mode==="append"} onChange={()=>setMode("append")} /> Hinzufügen</label>
            <label className="import-radio"><input type="radio" value="replace" checked={mode==="replace"} onChange={()=>setMode("replace")} /> Ersetzen <span style={{color:"#ef4444",fontSize:"0.8rem"}}>(löscht bestehende Daten!)</span></label>
          </div>

          <label className="import-confirm-check">
            <input type="checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} />
            <span>Ich habe alle Angaben kontrolliert und bestätige den Import von <b>{(preview.years?.length||0) * (preview.groups?.length||0)} Datenpunkten</b> als <b>«{meta.datensatz||"…"}»</b>.</span>
          </label>

          <div className="import-actions">
            <button className="btn-secondary" onClick={reset}>← Zurück</button>
            <button className="btn-primary" onClick={runImport} disabled={!confirmed||loading} style={!confirmed?{opacity:0.5}:{}}>
              {loading ? "Importiere…" : `Jetzt importieren →`}
            </button>
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div className="import-card">
          <h3>{!result.errors?.length ? "✅ Import erfolgreich!" : "⚠️ Import mit Fehlern"}</h3>
          <div className="import-preview-stats" style={{marginTop:16}}>
            <div className="admin-stat-card"><div className="admin-stat-val">{formatNumber(result.inserted, 0)}</div><div className="admin-stat-label">Zeilen importiert</div></div>
            {result.deleted>0&&<div className="admin-stat-card"><div className="admin-stat-val">{result.deleted}</div><div className="admin-stat-label">Zeilen gelöscht</div></div>}
            {result.skipped>0&&<div className="admin-stat-card"><div className="admin-stat-val">{result.skipped}</div><div className="admin-stat-label">Duplikate</div></div>}
          </div>
          <p style={{fontSize:"0.84rem",color:"var(--gray-700)",marginTop:16}}>Daten in Baserow gespeichert. KI-Such-Cache automatisch geleert.</p>
          <div className="import-actions"><button className="btn-primary" onClick={reset}>Neuer Import</button></div>
        </div>
      )}
    </div>
  );
}

function SdmxImport({ themenfelder }) {
  const [step, setStep] = useState("input");   // input | configure | done
  const [url, setUrl] = useState("");
  const [info, setInfo] = useState(null);       // Ergebnis von /inspect
  const [selection, setSelection] = useState({});
  const [meta, setMeta] = useState({ themenfeld:"", indikator:"", datensatz:"", quelle:"Bundesamt für Statistik (BFS) / stats.swiss" });
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => { loadStats(); }, []);
  const loadStats = async () => {
    try { const d = await api.sdmxListStats(); setStats(d.stats || []); } catch {}
  };

  const inspect = async () => {
    if (!url.trim()) { setError("Bitte URL oder ID eingeben"); return; }
    setLoading(true); setError("");
    try {
      const d = await api.sdmxInspect(url.trim());
      setInfo(d);
      setSelection(d.preset || {});
      setMeta(m => ({ ...m, datensatz: m.datensatz || d.title }));
      setStep("configure");
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const toggleValue = (dimId, code) => {
    setSelection(s => {
      const cur = s[dimId] || [];
      const next = cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code];
      const out = { ...s };
      if (next.length) out[dimId] = next; else delete out[dimId];
      return out;
    });
  };

  const register = async () => {
    if (!meta.themenfeld.trim()) { setError("Pflichtfeld: Themenfeld"); return; }
    if (!meta.indikator.trim())  { setError("Pflichtfeld: Indikator"); return; }
    if (!meta.datensatz.trim())  { setError("Pflichtfeld: Datensatz-Name"); return; }
    setLoading(true); setError("");
    try {
      await api.sdmxRegister({
        agency: info.agency, dataflow: info.dataflow,
        themenfeld: meta.themenfeld, indikator: meta.indikator,
        datensatz: meta.datensatz, quelle: meta.quelle,
        default_selection: selection,
        explorer_url: url.trim().startsWith("http") ? url.trim() : "",
      });
      setMsg(`«${meta.datensatz}» ist jetzt in der App verfügbar.`);
      setStep("done");
      loadStats();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Statistik aus der App entfernen?")) return;
    try { await api.sdmxDelete(id); setMsg("Entfernt."); loadStats(); }
    catch (e) { setError(e.message); }
  };

  const reset = () => {
    setStep("input"); setUrl(""); setInfo(null); setSelection({});
    setError(""); setMsg("");
    setMeta({ themenfeld:"", indikator:"", datensatz:"", quelle:"Bundesamt für Statistik (BFS) / stats.swiss" });
  };

  const STEPS = ["input","configure","done"];

  return (
    <div>
      <div className="import-stepper">
        {[["input","1. Statistik wählen"],["configure","2. Zuordnen & Vorauswahl"],["done","3. Fertig"]].map(([s,label])=>(
          <div key={s} className={`import-step ${step===s?"active":""} ${STEPS.indexOf(step)>STEPS.indexOf(s)?"done":""}`}>{label}</div>
        ))}
      </div>

      {error && <div className="admin-msg" style={{borderColor:"#ef4444",background:"rgba(239,68,68,0.08)",marginBottom:16}}>{error}</div>}
      {msg && <div className="admin-msg" style={{borderColor:"#10b981",background:"rgba(16,185,129,0.08)",marginBottom:16}}>{msg}</div>}

      {step === "input" && (
        <div className="import-card">
          <h3>stats.swiss Statistik einbinden</h3>
          <p style={{fontSize:"0.84rem",color:"var(--gray-700)",marginBottom:16,lineHeight:1.6}}>
            Die Statistik wird <b>live abgefragt</b> — es werden keine Daten importiert. Beim Öffnen holt die App genau die Werte die angezeigt werden, direkt von der offiziellen BFS-API. So sind die Daten immer aktuell und es gibt keine Grössenbeschränkung.
          </p>
          <div className="field" style={{marginBottom:14}}>
            <label>stats.swiss URL oder ID * <HelpTip text="Kopiere die URL aus der Adressleiste auf stats.swiss (stats.swiss/vis?...) oder die Datenabfrage-URL aus «Entwickler-API». Auch die Kurzform CH1.LWZ,DF_LWZ_1 funktioniert. Filter aus der URL werden als Vorauswahl übernommen." /></label>
            <input value={url} onChange={e=>{setUrl(e.target.value);setError("");}}
              placeholder="https://stats.swiss/vis?...  oder  CH1.LWZ,DF_LWZ_1"
              onKeyDown={e=>e.key==="Enter"&&inspect()} style={{width:"100%"}} />
          </div>
          <div className="import-actions" style={{marginTop:0}}>
            <button className="btn-primary" onClick={inspect} disabled={loading||!url.trim()}>
              {loading ? "Lade Metadaten…" : "Weiter →"}
            </button>
          </div>
        </div>
      )}

      {step === "configure" && info && (
        <div className="import-card">
          <h3>{info.title}</h3>
          <p style={{fontSize:"0.8rem",color:"var(--gray-400)",fontFamily:"monospace",marginBottom:18}}>{info.agency} / {info.dataflow}</p>

          <div className="import-fields" style={{marginBottom:18}}>
            <div className="field">
              <label>Themenfeld * <HelpTip text="Oberste Kategorie in der App-Suche, z.B. 'Bau und Wohnen'." /></label>
              <input list="tf-sdmx-list" value={meta.themenfeld} onChange={e=>setMeta(m=>({...m,themenfeld:e.target.value}))} placeholder="z.B. Bau und Wohnen" />
              <datalist id="tf-sdmx-list">{themenfelder.map(t=><option key={t} value={t}/>)}</datalist>
            </div>
            <div className="field">
              <label>Indikator *</label>
              <input value={meta.indikator} onChange={e=>setMeta(m=>({...m,indikator:e.target.value}))} placeholder="z.B. Leerwohnungen" />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Datensatz-Name * <HelpTip text="So erscheint die Statistik in der App-Suche." /></label>
              <input value={meta.datensatz} onChange={e=>setMeta(m=>({...m,datensatz:e.target.value}))} />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Quelle <HelpTip text="Erscheint in der Quellenangabe bei Excel- und JPEG-Export." /></label>
              <input value={meta.quelle} onChange={e=>setMeta(m=>({...m,quelle:e.target.value}))} />
            </div>
          </div>

          <div style={{marginBottom:8,fontSize:"0.85rem",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            Standard-Auswahl <HelpTip text="Was beim Öffnen der Statistik standardmässig angezeigt wird. Nichts angekreuzt = alle Werte dieser Dimension. Bei sehr grossen Dimensionen (z.B. alle Gemeinden) unbedingt eine Auswahl treffen, sonst wird die Abfrage langsam. Der Nutzer kann später in der App umstellen." />
          </div>
          <p style={{fontSize:"0.78rem",color:"var(--gray-400)",marginBottom:12}}>
            Tipp: bei der Geo-Dimension z.B. nur <b>Schaffhausen</b> und ein paar Vergleichskantone auswählen.
          </p>

          <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:18}}>
            {info.dimensions.map(d => (
              <SdmxDimensionPicker key={d.id} dim={d}
                selected={selection[d.id] || []}
                onToggle={code => toggleValue(d.id, code)}
                onClear={() => setSelection(s => { const o={...s}; delete o[d.id]; return o; })} />
            ))}
          </div>

          <div className="import-actions">
            <button className="btn-secondary" onClick={reset}>← Zurück</button>
            <button className="btn-primary" onClick={register} disabled={loading}>
              {loading ? "Speichere…" : "Statistik einbinden"}
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="import-card">
          <h3>✅ Statistik eingebunden</h3>
          <p style={{fontSize:"0.86rem",color:"var(--gray-700)",marginTop:12}}>
            «{meta.datensatz}» erscheint jetzt in der Suche und unter «Alle Statistiken». Die Daten werden bei jedem Öffnen live von stats.swiss geholt.
          </p>
          <div className="import-actions"><button className="btn-primary" onClick={reset}>Weitere einbinden</button></div>
        </div>
      )}

      {stats.length > 0 && (
        <div className="import-card" style={{marginTop:20}}>
          <h3>Eingebundene stats.swiss Statistiken ({stats.length})</h3>
          <div className="admin-log-table" style={{marginTop:12}}>
            <table>
              <thead><tr><th>Datensatz</th><th>Zuordnung</th><th>Dataflow</th><th></th></tr></thead>
              <tbody>
                {stats.map(s => (
                  <tr key={s.id}>
                    <td style={{fontWeight:600,fontSize:"0.83rem"}}>{s.datensatz}</td>
                    <td style={{fontSize:"0.8rem",color:"var(--gray-700)"}}>{s.themenfeld} → {s.indikator}</td>
                    <td style={{fontSize:"0.74rem",fontFamily:"monospace",color:"var(--gray-400)"}}>{s.id}</td>
                    <td><button onClick={()=>remove(s.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#ef4444"}}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SdmxDimensionPicker({ dim, selected, onToggle, onClear }) {
  // Hierarchische Dimensionen (z.B. Geo) in eine Box pro Ebene aufteilen
  if (dim.levels && dim.levels.length > 1) {
    const total = selected.length;
    return (
      <div style={{border:"1px solid rgba(17,17,17,0.12)",borderRadius:10,padding:"10px 12px",background:"rgba(17,17,17,0.02)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <span style={{fontWeight:700,fontSize:"0.84rem"}}>{dim.name}</span>
          <span style={{fontSize:"0.76rem",color:total?"var(--ink)":"var(--gray-400)",fontWeight:600}}>
            {total ? `${total} gewählt` : "alle"}
            {total > 0 && (
              <button className="btn-secondary" style={{fontSize:"0.7rem",padding:"2px 8px",marginLeft:8}} onClick={onClear}>
                Zurücksetzen
              </button>
            )}
          </span>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {dim.levels.map(lv => (
            <SdmxLevelBox key={lv.level} level={lv} selected={selected} onToggle={onToggle} />
          ))}
        </div>
      </div>
    );
  }
  return <SdmxLevelBox level={{name: dim.name, count: dim.count, options: dim.options}}
                       selected={selected} onToggle={onToggle} onClear={onClear} indent />;
}

function SdmxLevelBox({ level, selected, onToggle, onClear, indent }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const term = q.trim().toLowerCase();
  const filtered = term
    ? level.options.filter(o => o.label.toLowerCase().includes(term) || o.code.toLowerCase().includes(term))
    : level.options;
  const shown = filtered.slice(0, 300);
  const codes = level.options.map(o => o.code);
  const mine = selected.filter(c => codes.includes(c));

  return (
    <div style={{border:"1px solid rgba(17,17,17,0.1)",borderRadius:8,overflow:"hidden",background:"white"}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{padding:"8px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:"rgba(17,17,17,0.03)"}}>
        <div>
          <span style={{fontWeight:600,fontSize:"0.82rem"}}>{level.name}</span>
          <span style={{fontSize:"0.74rem",color:"var(--gray-400)",marginLeft:8}}>{level.count}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:"0.76rem",fontWeight:600,color:mine.length?"var(--ink)":"var(--gray-400)"}}>
            {mine.length ? `${mine.length} gewählt` : "–"}
          </span>
          <span style={{fontSize:"0.68rem"}}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {mine.length > 0 && !open && (
        <div style={{padding:"5px 12px",fontSize:"0.75rem",color:"var(--gray-700)",borderTop:"1px solid rgba(17,17,17,0.06)"}}>
          {mine.slice(0,6).map(code => {
            const o = level.options.find(x => x.code === code);
            return o ? o.label : code;
          }).join(", ")}{mine.length > 6 ? ` … +${mine.length-6}` : ""}
        </div>
      )}

      {open && (
        <div style={{padding:"8px 12px",borderTop:"1px solid rgba(17,17,17,0.07)"}}>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Suchen…"
              style={{flex:1,padding:"5px 10px",borderRadius:6,border:"1px solid rgba(17,17,17,0.15)",fontSize:"0.8rem",fontFamily:"inherit"}} />
            {onClear && mine.length > 0 && (
              <button className="btn-secondary" style={{fontSize:"0.74rem",padding:"4px 10px"}} onClick={onClear}>Zurücksetzen</button>
            )}
          </div>
          <div style={{maxHeight:200,overflowY:"auto",display:"flex",flexDirection:"column",gap:1}}>
            {shown.map(o => (
              <label key={o.code}
                style={{display:"flex",alignItems:"center",gap:8,fontSize:"0.8rem",padding:"3px 4px",cursor:"pointer"}}>
                <input type="checkbox" checked={selected.includes(o.code)} onChange={()=>onToggle(o.code)} />
                <span>{o.label}</span>
                <span style={{color:"var(--gray-400)",fontSize:"0.72rem",fontFamily:"monospace"}}>{o.code}</span>
              </label>
            ))}
            {filtered.length === 0 && <div style={{fontSize:"0.78rem",color:"var(--gray-400)",padding:6}}>Nichts gefunden</div>}
            {filtered.length > 300 && (
              <div style={{fontSize:"0.74rem",color:"var(--gray-400)",padding:6}}>
                … {filtered.length - 300} weitere — bitte suchen
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CsvImport({ themenfelder }) {
  const [step, setStep] = useState("upload");
  const [csvRows, setCsvRows] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [mode, setMode] = useState(""); // User muss wählen
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Metadaten — können überschrieben werden
  const [meta, setMeta] = useState({ themenfeld:"", indikator:"", datensatz:"", quelle:"", einheit:"" });

  // Aus Datei ermittelt
  const uniqueThemenfelder = [...new Set(csvRows.map(r => r["Themenfeld"]).filter(Boolean))];
  const uniqueIndikatoren  = [...new Set(csvRows.map(r => r["Indikator"]).filter(Boolean))];
  const uniqueDatensaetze  = [...new Set(csvRows.map(r => r["Datensatz"]).filter(Boolean))];
  const uniqueKantone      = [...new Set(csvRows.map(r => r["Kanton"]).filter(Boolean))];
  const uniqueJahre        = [...new Set(csvRows.map(r => r["Jahr"]).filter(Boolean))].sort();

  // Trennt eine CSV-Zeile zeichenweise, damit Anführungszeichen mit
  // Trennzeichen darin ("Bau, Wohnen") korrekt zusammenbleiben.
  const splitCsvLine = (line, delim) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === delim && !inQuotes) {
        out.push(cur); cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(v => v.trim());
  };

  // "1'234,5" / "1 234.5" / "1234" -> 1234.5
  const parseSwissNumber = (raw) => {
    if (raw === null || raw === undefined) return null;
    let s = String(raw).trim();
    if (!s) return null;
    s = s.replace(/['\u2019\s\u00a0]/g, "");        // Tausendertrenner weg
    if (s.includes(",") && s.includes(".")) {
      // beides vorhanden: das hintere Zeichen ist das Dezimaltrennzeichen
      s = s.lastIndexOf(",") > s.lastIndexOf(".")
        ? s.replace(/\./g, "").replace(",", ".")
        : s.replace(/,/g, "");
    } else if (s.includes(",")) {
      s = s.replace(",", ".");
    }
    const n = parseFloat(s);
    return isFinite(n) ? n : null;
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return { rows: [], error: "Datei leer oder nur Kopfzeile vorhanden." };

    // Trennzeichen automatisch erkennen — Excel (DE/CH) nutzt Semikolon,
    // andere Programme Komma oder Tabulator.
    const head = lines[0].replace(/^\uFEFF/, "");
    const counts = { ";": (head.match(/;/g) || []).length,
                     ",": (head.match(/,/g) || []).length,
                     "\t": (head.match(/\t/g) || []).length };
    const delim = Object.keys(counts).reduce((a, b) => counts[a] >= counts[b] ? a : b);
    if (counts[delim] === 0) {
      return { rows: [], error: "Kein Trennzeichen erkannt — erwartet wird Semikolon, Komma oder Tabulator." };
    }

    const headers = splitCsvLine(head, delim).map(h => h.replace(/^"|"$/g, "").replace(/^\uFEFF/, ""));

    // Spalten tolerant zuordnen (Gross-/Kleinschreibung, Leerzeichen egal)
    const norm = (s) => String(s).toLowerCase().replace(/[^a-zäöü0-9]/g, "");
    const findCol = (...namen) => headers.find(h => namen.some(n => norm(h) === norm(n)));
    const colJahr = findCol("Jahr");
    const colWert = findCol("Wert", "Value");

    if (!colJahr || !colWert) {
      return { rows: [], error:
        `Pflichtspalten fehlen. Gefunden: ${headers.join(", ") || "(keine)"} — benötigt: Jahr und Wert.` };
    }

    const rows = [];
    let uebersprungen = 0;
    lines.slice(1).forEach(line => {
      const vals = splitCsvLine(line, delim);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/^"|"$/g, ""); });

      const jahr = String(obj[colJahr] || "").trim();
      const wert = parseSwissNumber(obj[colWert]);
      if (!jahr || wert === null) { uebersprungen++; return; }

      obj["Jahr"] = jahr;
      obj["Wert"] = wert;
      rows.push(obj);
    });

    if (rows.length === 0) {
      return { rows: [], error:
        `Keine gültigen Zeilen gefunden (${uebersprungen} übersprungen). ` +
        `Jede Zeile braucht ein Jahr und einen Zahlenwert.` };
    }
    return { rows, error: "", skipped: uebersprungen };
  };

  const handleFile = (file) => {
    if (!file) return;
    setCsvError("");
    const ext = file.name.split(".").pop().toLowerCase();
    if (["xlsx", "xls"].includes(ext)) {
      setCsvError("Excel-Dateien bitte zuerst als CSV speichern: Datei → Speichern unter → «CSV UTF-8 (durch Trennzeichen getrennt)».");
      return;
    }
    if (!["csv", "txt", "tsv"].includes(ext)) {
      setCsvError(`Dateityp .${ext} wird nicht unterstützt — bitte eine CSV-Datei hochladen.`);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setCsvError("Datei konnte nicht gelesen werden.");
    reader.onload = e => {
      let text = e.target.result;

      // Umlaute prüfen: sieht der Text nach falscher Kodierung aus, nochmal
      // als Windows-1252 lesen (Excel speichert manchmal ohne UTF-8).
      if (/Ã¤|Ã¶|Ã¼|Ã\u009F|ï¿½/.test(text)) {
        const r2 = new FileReader();
        r2.onload = e2 => finish(e2.target.result, file);
        r2.readAsText(file, "windows-1252");
        return;
      }
      finish(text, file);
    };

    const finish = (text, f) => {
      const { rows, error, skipped } = parseCSV(text);
      if (error) { setCsvError(error); return; }
      setCsvRows(rows);
      if (skipped) {
        setCsvError(`Hinweis: ${skipped} Zeile(n) ohne Jahr oder Zahlenwert wurden übersprungen.`);
      }
      const erste = (spalte) => [...new Set(rows.map(r => r[spalte]).filter(Boolean))][0] || "";
      setMeta({
        themenfeld: erste("Themenfeld"),
        indikator:  erste("Indikator"),
        datensatz:  erste("Datensatz"),
        quelle: "",
        einheit: erste("Einheit"),
      });
      setStep("meta");
    };

    reader.readAsText(file, "UTF-8");
  };

  const runImport = async () => {
    if (!confirmed) return;
    setLoading(true);
    try {
      const r = await api.importRun({
        password: "unused",
        themenfeld: meta.themenfeld,
        indikator: meta.indikator,
        datensatz: meta.datensatz,
        quelle: meta.quelle,
        mode, rows: csvRows,
      });
      setResult(r);
      setStep("result");
    } catch (e) { setCsvError(e.message); }
    setLoading(false);
  };

  const reset = () => {
    setStep("upload"); setCsvRows([]); setCsvError("");
    setResult(null); setConfirmed(false);
    setMeta({ themenfeld:"", indikator:"", datensatz:"", quelle:"", einheit:"" });
  };

  const STEPS = ["upload","meta","confirm","result"];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
        <button className="btn-secondary" onClick={() => api.downloadTemplate()}>⬇ CSV-Vorlage</button>
      </div>

      <div className="import-stepper">
        {[["upload","1. Datei"],["meta","2. Metadaten"],["confirm","3. Kontrolle"],["result","4. Ergebnis"]].map(([s,label])=>(
          <div key={s} className={`import-step ${step===s?"active":""} ${STEPS.indexOf(step)>STEPS.indexOf(s)?"done":""}`}>{label}</div>
        ))}
      </div>

      {csvError && <div className="admin-msg" style={{borderColor:"#ef4444",background:"rgba(239,68,68,0.08)",marginBottom:16}}>{csvError}</div>}

      {/* Schritt 1: Upload */}
      {step === "upload" && (
        <div className="import-card">
          <div className="import-dropzone"
            onDragOver={e=>e.preventDefault()}
            onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}}>
            <div className="import-dropzone-icon">📂</div>
            <p>CSV-Datei hier ablegen oder</p>
            <label className="btn-primary" style={{cursor:"pointer"}}>
              Datei wählen
              <input type="file" accept=".csv" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])} />
            </label>
            <div style={{fontSize:"0.78rem",color:"var(--gray-400)",marginTop:8,textAlign:"center",lineHeight:1.6}}>
              Erwartete Spalten: <b>Themenfeld, Indikator, Datensatz, Kanton, Filter 1, Filteroptionen 1, Jahr, Wert</b><br/>
              Pflicht sind nur <b>Jahr</b> und <b>Wert</b>. Semikolon, Komma und Tabulator werden automatisch erkannt.<br/>
              Excel → Datei → Speichern unter → «CSV UTF-8 (durch Trennzeichen getrennt)»
            </div>
          </div>
        </div>
      )}

      {/* Schritt 2: Metadaten */}
      {step === "meta" && (
        <div className="import-card">
          <h3>Metadaten prüfen & ergänzen</h3>
          <p style={{fontSize:"0.84rem",color:"var(--gray-700)",marginBottom:20}}>
            {csvRows.length} Zeilen geladen. Prüfe und ergänze die Metadaten — diese werden in Baserow gespeichert.
          </p>
          <div className="import-fields">
            <div className="field">
              <label>Themenfeld *</label>
              <input list="tf-list" value={meta.themenfeld} onChange={e=>setMeta(m=>({...m,themenfeld:e.target.value}))} placeholder="z.B. Arbeit" />
              <datalist id="tf-list">{themenfelder.map(t=><option key={t} value={t}/>)}</datalist>
            </div>
            <div className="field">
              <label>Indikator *</label>
              <input value={meta.indikator} onChange={e=>setMeta(m=>({...m,indikator:e.target.value}))} placeholder="z.B. Beschäftigung" />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Datensatz-Name * <span style={{fontWeight:400,color:"var(--gray-400)"}}>— so erscheint er in der App</span></label>
              <input value={meta.datensatz} onChange={e=>setMeta(m=>({...m,datensatz:e.target.value}))} placeholder="z.B. Beschäftigte nach Kanton & Wirtschaftssektor" />
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>Quelle * <span style={{fontWeight:400,color:"var(--gray-400)"}}>— z.B. "BFS STATENT + www.bfs.admin.ch"</span></label>
              <input value={meta.quelle} onChange={e=>setMeta(m=>({...m,quelle:e.target.value}))} placeholder="Herausgeber + URL" />
            </div>
            <div className="field">
              <label>Einheit <span style={{fontWeight:400,color:"var(--gray-400)"}}>— optional</span></label>
              <input value={meta.einheit} onChange={e=>setMeta(m=>({...m,einheit:e.target.value}))} placeholder="z.B. Anzahl, Prozent, CHF" />
            </div>
          </div>

          <div className="import-mode">
            <label><b>Import-Modus: *</b> <HelpTip text="Hinzufügen: neue Zeilen werden ergänzt, bestehende Daten bleiben. Ersetzen: alle bestehenden Zeilen für diesen Datensatz werden zuerst gelöscht, dann neu importiert." /></label>
            <label className="import-radio">
              <input type="radio" value="append" checked={mode==="append"} onChange={()=>setMode("append")} />
              Hinzufügen <span style={{color:"var(--gray-400)",fontSize:"0.8rem"}}>(bestehende behalten, Duplikate werden übersprungen)</span>
            </label>
            <label className="import-radio">
              <input type="radio" value="replace" checked={mode==="replace"} onChange={()=>setMode("replace")} />
              Ersetzen <span style={{color:"#ef4444",fontSize:"0.8rem"}}>(bestehende Daten für diesen Datensatz werden zuerst gelöscht!)</span>
            </label>
            {!mode && <div style={{fontSize:"0.78rem",color:"#ef4444",marginTop:4}}>⚠ Bitte Import-Modus wählen</div>}
          </div>

          <div className="import-actions">
            <button className="btn-secondary" onClick={reset}>← Zurück</button>
            <button className="btn-primary"
              onClick={() => {
                if (!meta.themenfeld.trim()) { setCsvError("Pflichtfeld fehlt: Themenfeld"); return; }
                if (!meta.indikator.trim())  { setCsvError("Pflichtfeld fehlt: Indikator"); return; }
                if (!meta.datensatz.trim())  { setCsvError("Pflichtfeld fehlt: Datensatz-Name"); return; }
                if (!meta.quelle.trim())     { setCsvError("Pflichtfeld fehlt: Quelle"); return; }
                if (!mode)                   { setCsvError("Pflichtfeld fehlt: Import-Modus wählen (Hinzufügen oder Ersetzen)"); return; }
                setCsvError("");
                setStep("confirm");
              }}>
              Weiter zur Kontrolle →
            </button>
          </div>
        </div>
      )}

      {/* Schritt 3: Kontrolle */}
      {step === "confirm" && (
        <div className="import-card">
          <h3>Kontrollübersicht vor dem Import</h3>
          <div className="import-summary-grid">
            <div className="import-summary-item">
              <div className="import-summary-label">Zeilen total</div>
              <div className="import-summary-val">{formatNumber(csvRows.length, 0)}</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Jahre</div>
              <div className="import-summary-val">{uniqueJahre[0]}–{uniqueJahre[uniqueJahre.length-1]} ({uniqueJahre.length})</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Themenfeld</div>
              <div className="import-summary-val">{meta.themenfeld}</div>
            </div>
            <div className="import-summary-item">
              <div className="import-summary-label">Indikator</div>
              <div className="import-summary-val">{meta.indikator}</div>
            </div>
            <div className="import-summary-item" style={{gridColumn:"1/-1"}}>
              <div className="import-summary-label">Datensatz-Name</div>
              <div className="import-summary-val">{meta.datensatz}</div>
            </div>
            {meta.quelle && <div className="import-summary-item" style={{gridColumn:"1/-1"}}>
              <div className="import-summary-label">Quelle</div>
              <div className="import-summary-val">{meta.quelle}</div>
            </div>}
            {uniqueKantone.length > 0 && (
              <div className="import-summary-item" style={{gridColumn:"1/-1"}}>
                <div className="import-summary-label">Kantone / Gemeinden ({uniqueKantone.length})</div>
                <div className="import-summary-val" style={{fontSize:"0.82rem"}}>{uniqueKantone.slice(0,12).join(", ")}{uniqueKantone.length>12?` … +${uniqueKantone.length-12} weitere`:""}</div>
              </div>
            )}
            <div className="import-summary-item">
              <div className="import-summary-label">Modus</div>
              <div className="import-summary-val" style={{color:mode==="replace"?"#ef4444":undefined}}>{mode==="replace"?"Ersetzen ⚠️":"Hinzufügen"}</div>
            </div>
          </div>

          <div style={{marginTop:4,marginBottom:8,fontWeight:700,fontSize:"0.85rem"}}>Erste 5 Zeilen:</div>
          <div className="admin-log-table" style={{marginBottom:20}}>
            <table>
              <thead><tr>{Object.keys(csvRows[0]||{}).map(k=><th key={k}>{k}</th>)}</tr></thead>
              <tbody>{csvRows.slice(0,5).map((r,i)=><tr key={i}>{Object.values(r).map((v,j)=><td key={j}>{v}</td>)}</tr>)}</tbody>
            </table>
          </div>

          <label className="import-confirm-check" title="Bitte alle Angaben nochmals prüfen bevor du importierst">
            <input type="checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} />
            <span>Ich habe alle Angaben kontrolliert und bestätige den Import von <b>{formatNumber(csvRows.length, 0)} Zeilen</b> als <b>«{meta.datensatz}»</b>{mode==="replace"?" (bestehende Daten werden gelöscht!)":""}.</span>
          </label>

          <div className="import-actions">
            <button className="btn-secondary" onClick={()=>setStep("meta")}>← Zurück</button>
            <button className="btn-primary" onClick={runImport} disabled={!confirmed||loading}
              style={!confirmed?{opacity:0.5}:{}}>
              {loading?"Importiere…":`Jetzt importieren (${csvRows.length} Zeilen)`}
            </button>
          </div>
        </div>
      )}

      {/* Schritt 4: Ergebnis */}
      {step === "result" && result && (
        <div className="import-card">
          <h3>{!result.errors?.length?"✅ Import erfolgreich!":"⚠️ Import mit Fehlern"}</h3>
          <div className="import-preview-stats" style={{marginTop:16}}>
            <div className="admin-stat-card"><div className="admin-stat-val">{formatNumber(result.inserted, 0)}</div><div className="admin-stat-label">Zeilen importiert</div></div>
            {result.deleted>0&&<div className="admin-stat-card"><div className="admin-stat-val">{result.deleted}</div><div className="admin-stat-label">Zeilen gelöscht</div></div>}
            {result.skipped>0&&<div className="admin-stat-card"><div className="admin-stat-val">{result.skipped}</div><div className="admin-stat-label">Duplikate übersprungen</div></div>}
            {result.errors?.length>0&&<div className="admin-stat-card" style={{background:"rgba(239,68,68,0.1)"}}><div className="admin-stat-val" style={{color:"#ef4444"}}>{result.errors.length}</div><div className="admin-stat-label">Fehler</div></div>}
          </div>
          {result.errors?.length>0&&<div className="admin-msg" style={{marginTop:12,borderColor:"#ef4444"}}>{result.errors.join(" | ")}</div>}
          <p style={{fontSize:"0.84rem",color:"var(--gray-700)",marginTop:16}}>Die Daten sind jetzt in Baserow verfügbar. KI-Such-Cache wurde automatisch geleert.</p>
          <div className="import-actions"><button className="btn-primary" onClick={reset}>Neuer Import</button></div>
        </div>
      )}
    </div>
  );
}

// ---------- Admin Portal ----------
function AdminPortal({ onChatbotChange }) {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [activeTab, setActiveTab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("alle");
  const [tokens, setTokens] = useState(null);
  const [chatbotOn, setChatbotOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const login = async () => {
    try {
      await api.adminLogin(pw);
      setAuthed(true);
      setPwError(false);
      loadTab("logs");
    } catch {
      setPwError(true);
    }
  };

  const loadTab = async (tab) => {
    setActiveTab(tab);
    setLoading(true);
    setMsg("");
    try {
      if (tab === "logs") {
        const d = await api.adminLogs(pw);
        setLogs(d.logs || []);
      } else if (tab === "tokens") {
        const d = await api.adminTokens(pw);
        setTokens(d);
      } else if (tab === "settings") {
        const d = await api.getChatbotStatus();
        setChatbotOn(d.enabled);
      }
    } catch (e) { setMsg("Fehler: " + e.message); }
    setLoading(false);
  };

  const toggleChatbot = async (val) => {
    try {
      await api.adminSetChatbot(pw, val);
      setChatbotOn(val);
      onChatbotChange(val);
      setMsg(val ? "Chatbot aktiviert." : "Chatbot deaktiviert.");
    } catch (e) { setMsg("Fehler: " + e.message); }
  };

  const clearCache = async () => {
    try {
      const d = await api.adminClearCache(pw);
      setMsg(`Cache geleert (${d.cleared} Einträge entfernt).`);
    } catch (e) { setMsg("Fehler: " + e.message); }
  };

  const EVENT_LABELS = {
    login: "🔑 Anmeldung",
    logout: "🚪 Abmeldung",
    search_claude: "🔍 KI-Suche",
    search_claude_result: "🔍 KI-Suche Ergebnis",
    search_cache_hit: "⚡ Cache-Treffer",
    view_stat: "📊 Statistik angeschaut",
    export_excel: "📥 Excel-Export",
    chat_query: "💬 Chat-Frage",
    chat_answer: "🤖 Chat-Antwort",
    import: "⬆ Daten-Import",
    import_undo: "↩ Import rückgängig",
    datensatz_delete: "🗑 Statistik gelöscht",
    sdmx_register: "🌐 stats.swiss eingebunden",
    sdmx_delete: "🌐 stats.swiss entfernt",
    link_add: "🔗 Verweis hinzugefügt",
  };

  if (!authed) return (
    <div className="admin-login">
      <div className="admin-login-card">
        <h2>⚙ Admin-Portal</h2>
        <p>Bitte Passwort eingeben:</p>
        <input
          type="password" value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && login()}
          placeholder="Passwort"
          className={pwError ? "admin-pw-error" : ""}
          autoFocus
        />
        {pwError && <div className="admin-error">Falsches Passwort</div>}
        <button className="btn-primary" onClick={login}>Anmelden</button>
      </div>
    </div>
  );

  return (
    <div className="admin-portal">
      <div className="admin-tabs">
        {[["logs","📋 Aktivitäts-Log"],["tokens","💰 Token-Kosten"],["roles","👥 Rollen"],["imports","📥 Imports"],["settings","⚙ Einstellungen"]].map(([id,label])=>(
          <button key={id} className={`admin-tab ${activeTab===id?"active":""}`} onClick={()=>loadTab(id)}>{label}</button>
        ))}
      </div>

      {msg && <div className="admin-msg">{msg}</div>}
      {loading && <div className="admin-msg">Lädt…</div>}

      {activeTab === "logs" && !loading && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3>Aktivitäts-Log <span className="admin-count">{logs.length} Einträge</span></h3>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <select value={logFilter} onChange={e=>setLogFilter(e.target.value)}
                style={{padding:"5px 10px",borderRadius:8,border:"1px solid rgba(17,17,17,0.12)",fontFamily:"inherit",fontSize:"0.82rem",background:"white"}}>
                <option value="alle">Alle Ereignisse</option>
                <option value="auth">Nur An-/Abmeldungen</option>
                <option value="search">Nur Suchen</option>
                <option value="data">Nur Import & Löschen</option>
              </select>
              <button className="btn-secondary" onClick={() => loadTab("logs")}>↻ Aktualisieren</button>
            </div>
          </div>
          <div className="admin-log-table">
            <table>
              <thead><tr><th>Zeit</th><th>Benutzer</th><th>Event</th><th>Details</th></tr></thead>
              <tbody>
                {logs.filter(l => {
                  if (logFilter === "auth") return ["login","logout"].includes(l.event);
                  if (logFilter === "search") return String(l.event || "").startsWith("search") || l.event === "view_stat";
                  if (logFilter === "data") return ["import","import_undo","datensatz_delete","sdmx_register","sdmx_delete"].includes(l.event);
                  return true;
                }).map((l, i) => (
                  <tr key={i}>
                    <td className="admin-log-ts">{l.ts ? new Date(l.ts).toLocaleString("de-CH") : "—"}</td>
                    <td className="admin-log-user">
                      {l.name || l.user
                        ? <><div style={{fontWeight:600}}>{l.name || "—"}</div>
                            {l.user && <div style={{fontSize:"0.72rem",color:"var(--gray-400)"}}>{l.user}</div>}</>
                        : <span style={{color:"var(--gray-400)"}}>—</span>}
                    </td>
                    <td><span className="admin-log-badge">{EVENT_LABELS[l.event] || l.event}</span></td>
                    <td className="admin-log-detail">
                      {l.query && <span>«{l.query}»</span>}
                      {l.question && <span>«{l.question}»</span>}
                      {l.datensatz && <span>{l.datensatz}</span>}
                      {l.table_id && <span>{l.table_id}</span>}
                      {l.stat_id && <span>{l.stat_id}</span>}
                      {l.stat && <span>{l.stat}</span>}
                      {l.n_matches !== undefined && <span>{l.n_matches} Treffer</span>}
                      {l.inserted !== undefined && <span>{formatNumber(l.inserted, 0)} Zeilen</span>}
                      {l.deleted !== undefined && l.deleted > 0 && <span>{formatNumber(l.deleted, 0)} gelöscht</span>}
                      {l.source && !l.query && <span className={`source-badge ${l.source}`}>{l.source === "bfs" ? "BFS" : l.source === "sdmx" ? "stats.swiss" : "Eigene"}</span>}
                      {l.raw && <span style={{color:"var(--gray-400)"}}>{l.raw}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "tokens" && tokens && !loading && (
        <div className="admin-section">
          <h3>Token-Verbrauch & Kosten</h3>
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-val">{tokens.total_calls}</div>
              <div className="admin-stat-label">Claude-Aufrufe total</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-val">{(tokens.total_input/1000).toFixed(1)}k</div>
              <div className="admin-stat-label">Input-Tokens</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-val">{(tokens.total_output/1000).toFixed(1)}k</div>
              <div className="admin-stat-label">Output-Tokens</div>
            </div>
            <div className="admin-stat-card highlight">
              <div className="admin-stat-val">${tokens.total_cost_usd.toFixed(4)}</div>
              <div className="admin-stat-label">Kosten total (USD)</div>
            </div>
          </div>
          <h4 style={{margin:"20px 0 8px",fontSize:"0.88rem",color:"var(--gray-700)"}}>Nach Typ</h4>
          <table className="admin-token-table">
            <thead><tr><th>Typ</th><th>Aufrufe</th><th>Input</th><th>Output</th><th>Kosten USD</th></tr></thead>
            <tbody>
              {Object.entries(tokens.by_type).map(([type, d]) => (
                <tr key={type}>
                  <td>{EVENT_LABELS["search_"+type] || type}</td>
                  <td>{d.calls}</td>
                  <td>{formatNumber(d.input, 0)}</td>
                  <td>{formatNumber(d.output, 0)}</td>
                  <td>${d.cost_usd.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{fontSize:"0.75rem",color:"var(--gray-400)",marginTop:8}}>
            Preise: $3/M Input-Token, $15/M Output-Token (Claude Sonnet 4.6). Zurückgesetzt bei Server-Neustart.
          </p>
        </div>
      )}

      {activeTab === "roles" && !loading && (
        <RolesTab pw={pw} msg={msg} setMsg={setMsg} />
      )}

      {activeTab === "imports" && !loading && (
        <ImportsTab pw={pw} msg={msg} setMsg={setMsg} />
      )}

      {activeTab === "settings" && !loading && (
        <div className="admin-section">
          <h3>Einstellungen</h3>
          <div className="admin-setting-row">
            <div>
              <div className="admin-setting-label">Daten-Assistent (Chatbot) <HelpTip text="Blendet den Chat-Bot (💬 unten rechts) für alle User ein oder aus. Kann jederzeit wieder aktiviert werden." /></div>
              <div className="admin-setting-desc">Blendet den Chat-Bot unten rechts für alle Nutzer ein oder aus.</div>
            </div>
            <label className="ai-toggle">
              <input type="checkbox" checked={chatbotOn} onChange={e => toggleChatbot(e.target.checked)} />
              <span className="ai-toggle-track"><span className="ai-toggle-thumb"/></span>
            </label>
          </div>
          <div className="admin-setting-row">
            <div>
              <div className="admin-setting-label">KI-Such-Cache leeren <HelpTip text="Nach Datenaktualisierungen in Baserow leeren — damit neue Datensätze sofort in der KI-Suche erscheinen. Passiert beim Import automatisch." /></div>
              <div className="admin-setting-desc">Nach jährlicher Datenaktualisierung in Baserow — damit neue Datensätze in der Suche erscheinen.</div>
            </div>
            <button className="btn-secondary" onClick={clearCache}>Cache leeren</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- ChatBot Widget ----------
function ChatBot({ onNavigateToChat }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");

  const handleSend = () => {
    if (!input.trim()) return;
    const question = input.trim();
    setInput("");
    setOpen(false);
    onNavigateToChat(question);
  };

  return (
    <>
      <button
        className={`chatbot-fab ${open ? "open" : ""}`}
        onClick={() => setOpen(v => !v)}
        title="Daten-Assistent öffnen"
      >
        {open ? "✕" : "💬"}
      </button>

      {open && (
        <div className="chatbot-panel">
          <div className="chatbot-header">
            <span className="chatbot-header-title">🤖 Daten-Assistent</span>
            <span className="chatbot-header-sub">Enter → öffnet volle Chat-Seite</span>
          </div>
          <div className="chatbot-messages">
            <div className="chatbot-msg assistant">
              <div className="chatbot-bubble">
                Hallo! Stell mir eine Frage zu Wirtschaftsdaten, z.B.:<br/>
                <em>«Wie viele Arbeitslosen gab es 2014?»</em>
              </div>
            </div>
          </div>
          <div className="chatbot-input-row">
            <input
              className="chatbot-input"
              placeholder="Frage stellen…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSend()}
              autoFocus
            />
            <button className="chatbot-send" onClick={handleSend}>→</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  const [step, setStep] = useState(STEP.SEARCH);
  const [previousStep, setPreviousStep] = useState(STEP.SEARCH);

  const [query, setQuery] = useState("");
  const [queryHints, setQueryHints] = useState([]); // lokale Suchvorschläge beim Tippen
  const [showHints, setShowHints] = useState(false);
  const searchRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(true);
  const [pendingChatQuestion, setPendingChatQuestion] = useState(null);
  const [pendingChatId, setPendingChatId] = useState(null); // welcher Chat aktiv war
  const [currentUser, setCurrentUser] = useState(null); // null=prüfen, false=nicht eingeloggt, obj=eingeloggt
  const [authChecked, setAuthChecked] = useState(false);
  const [myRole, setMyRole] = useState("user");

  // Advanced-Search Optionen — Themenfeld/Indikator sind reine String-Listen
  // (kommen aus beiden Quellen gemischt). datensaetzeList enthält Objekte
  // {source, datensatz, table_id?, ...}, da hier die Quelle pro Eintrag
  // unterschiedlich sein kann (eigene DB vs. BFS) — kein Schalter mehr nötig.
  const [themenfelder, setThemenfelder] = useState([]);
  const [indikatorenList, setIndikatorenList] = useState([]);
  const [datensaetzeList, setDatensaetzeList] = useState([]);

  // Vorab-Filter in der Suchleiste (Advanced Search)
  const [preFilters, setPreFilters] = useState({
    themenfeld: "",
    indikator: "",
    datensatz: "",
  });

  // Die gewählte Statistik
  const [selectedStat, setSelectedStat] = useState(null);
  const [sdmxDimensions, setSdmxDimensions] = useState([]);
  const [sdmxSelection, setSdmxSelection] = useState({}); // { source, themenfeld, indikator, datensatz, bfs_table_id, bfs_title }

  // Filter, die NUR für die aktuell gewählte Statistik gelten (dynamisch vom Backend befüllt)
  const [refineGemeinde, setRefineGemeinde] = useState([]);
  const [refineYearFrom, setRefineYearFrom] = useState("");
  const [refineYearTo, setRefineYearTo] = useState("");
  const [refineFilterkategorie, setRefineFilterkategorie] = useState([]);
  const [refineJahre, setRefineJahre] = useState([]); // für Monatsdaten: welche Jahre anzeigen
  const [isMonthly, setIsMonthly] = useState(false);
  const [refineFilterWert, setRefineFilterWert] = useState([]);
  const [availableFilters, setAvailableFilters] = useState(null); // { gemeinden, filterkategorien, filter_values }

  // BFS-spezifisch: zwei kaskadierende Dimension+Werte Filter (MEHRFACHAUSWAHL)
  const [bfsDimensions, setBfsDimensions] = useState([]); // [{code, text, options:[{value,label}]}]
  const [quelleInfo, setQuelleInfo] = useState(null); // { herausgeber, titel, link }
  const [bfsFilter1Code, setBfsFilter1Code] = useState("");
  const [bfsFilter1Values, setBfsFilter1Values] = useState([]);
  const [bfsFilter2Code, setBfsFilter2Code] = useState("");
  const [bfsFilter2Values, setBfsFilter2Values] = useState([]);
  // Geo-Hierarchie: separate Auswahl für Kanton/Bezirk/Gemeinde
  const [geoFilter, setGeoFilter] = useState({}); // { kanton: [], bezirk: [], gemeinde: [] }

  const [suggestions, setSuggestions] = useState(null); // { matches, gemeinde, erklaerung }
  const [results, setResults] = useState(null);
  const [displayMode, setDisplayMode] = useState("original");
  const [baseYear, setBaseYear] = useState(null);
  const [chartType, setChartType] = useState("line");
  const [yMin, setYMin] = useState("");
  const [yMax, setYMax] = useState("");
  const [allYears, setAllYears] = useState([]); // ungefilterte Jahr-Liste für Min/Max-Anzeige
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTitle, setLoadingTitle] = useState("Lädt…");
  const [loadingSubtext, setLoadingSubtext] = useState("");

  // Zeigt den Glass-Loading-Overlay mit einer kurzen Erklärung, was gerade
  // passiert (Claude-Anfrage, BFS-Abfrage, Baserow-Suche etc.) — macht
  // Wartezeiten nachvollziehbar statt eines anonymen Spinners.
  const startLoading = (title, subtext = "") => {
    setLoadingTitle(title);
    setLoadingSubtext(subtext);
    setLoading(true);
  };
  const chartRef = useRef(null);

  useEffect(() => {
    api.getThemenfelder().then(setThemenfelder).catch(() => {});
    api.getChatbotStatus().then(d => setChatbotEnabled(d.enabled)).catch(() => {});
    api.getCardPrefs().then(setCardPrefs).catch(() => {});

    // Auth prüfen
    api.getMe().then(d => {
      setCurrentUser(d ? d.user : false);
      setAuthChecked(true);
      if (d) api.getMyRole().then(r => setMyRole(r.role || "user")).catch(() => {});
    }).catch(() => {
      setCurrentUser(false);
      setAuthChecked(true);
    });

    // Statistik aus URL-Hash wiederherstellen (nach Page-Reload)
    try {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const match = JSON.parse(decodeURIComponent(hash));
        if (match && match.source) {
          selectStat(match);
        }
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Schliesse die Hint-Liste wenn ausserhalb des Suchfelds geklickt wird
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowHints(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    api.getIndikatoren(preFilters.themenfeld || undefined)
      .then(setIndikatorenList)
      .catch((e) => { console.error("Indikatoren laden fehlgeschlagen:", e); setIndikatorenList([]); });
  }, [preFilters.themenfeld]);

  // Komplette, UNGEFILTERTE Liste aller Datensätze (beide Quellen) — wird für
  // die "rückwärts"-Suche gebraucht: man kann direkt im Datensatz-Feld tippen,
  // ohne vorher Themenfeld/Indikator zu wählen, und beide werden dann
  // automatisch passend gesetzt (siehe chooseDatensatzDirectly).
  const [allDatensaetze, setAllDatensaetze] = useState([]);
  const [sdmxStats, setSdmxStats] = useState([]);
  const [overviewSearch, setOverviewSearch] = useState("");
  const [cardPrefs, setCardPrefs] = useState({});
  const [editingCard, setEditingCard] = useState(null); // Suche auf der Übersichtsseite
  const [pendingAiTerms, setPendingAiTerms] = useState([]); // Filter-Stichwörter aus der KI-Suche
  const [deleteTarget, setDeleteTarget] = useState(null);   // Statistik die gelöscht werden soll
  const [deleteInfo, setDeleteInfo] = useState(null);       // Vorschau vom Backend
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    api.getDatensaetze()
      .then(setAllDatensaetze)
      .catch((e) => { console.error("Alle Datensätze laden fehlgeschlagen:", e); setAllDatensaetze([]); });
    api.sdmxListStats()
      .then(d => setSdmxStats((d.stats || []).map(s => ({ ...s, source: "sdmx" }))))
      .catch(() => setSdmxStats([]));
  }, []);

  useEffect(() => {
    if (preFilters.themenfeld || preFilters.indikator) {
      api
        .getDatensaetze(preFilters.themenfeld || undefined, preFilters.indikator || undefined)
        .then(setDatensaetzeList)
        .catch((e) => { console.error("Datensätze laden fehlgeschlagen:", e); setDatensaetzeList([]); });
    } else {
      setDatensaetzeList(allDatensaetze);
    }
  }, [preFilters.themenfeld, preFilters.indikator, allDatensaetze]);

  const updatePreFilter = (key, value) => {
    setPreFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "themenfeld") {
        next.indikator = "";
        next.datensatz = "";
      }
      if (key === "indikator") {
        next.datensatz = "";
      }
      return next;
    });
  };

  // Setzt die Advanced-Search-Filter (Themenfeld/Indikator/Datensatz) auf
  // der Startseite zurück, ohne dass die Seite neu geladen werden muss.
  const resetPreFilters = () => {
    setPreFilters({ themenfeld: "", indikator: "", datensatz: "" });
    setSuggestions(null);
    setStatus("");
  };

  // "Rückwärts"-Auswahl: Nutzer wählt direkt einen Datensatz, OHNE vorher
  // Themenfeld/Indikator gesetzt zu haben -> beide werden automatisch aus
  // dem gewählten Eintrag übernommen.
  const chooseDatensatzDirectly = (datensatzValue) => {
    if (!datensatzValue) {
      setPreFilters((prev) => ({ ...prev, datensatz: "" }));
      return;
    }
    const chosen = allDatensaetze.find((d) => d.datensatz === datensatzValue);
    setPreFilters({
      themenfeld: chosen?.themenfeld || "",
      indikator: chosen?.indikator || "",
      datensatz: datensatzValue,
    });
  };

  // ---------- Schritt 1: Suche ----------

  const handleSearch = async () => {
    // Wenn Suchtext eingegeben → immer KI-Suche, Filter werden ignoriert/zurückgesetzt
    if (query.trim()) {
      await handleAiSearch();
      return;
    }

    // Fall 1: konkreter Datensatz gewählt -> direkt laden
    if (preFilters.datensatz) {
      const chosen = datensaetzeList.find((d) => d.datensatz === preFilters.datensatz);
      if (chosen?.source === "bfs") {
        selectStat({ source: "bfs", bfs_table_id: chosen.table_id, bfs_title: chosen.title });
      } else {
        selectStat({
          source: "eigene_db",
          themenfeld: preFilters.themenfeld,
          indikator: preFilters.indikator,
          datensatz: preFilters.datensatz,
        });
      }
      return;
    }

    // Fall 2: nur Themenfeld (und/oder Indikator) gewählt, kein konkreter
    // Datensatz -> zeige alle dazu passenden Optionen als Vorschlagskarten
    // (datensaetzeList ist bereits nach Themenfeld/Indikator gefiltert).
    if (preFilters.themenfeld || preFilters.indikator) {
      const matches = datensaetzeList.map((d) => ({
        source: d.source,
        themenfeld: preFilters.themenfeld,
        indikator: d.indikator || preFilters.indikator,
        datensatz: d.datensatz,
        bfs_table_id: d.table_id,
        bfs_title: d.title,
      }));
      setSuggestions({
        matches,
        gemeinde: null,
        erklaerung: matches.length
          ? `${matches.length} passende Statistiken gefunden — wähle eine aus.`
          : "Keine passenden Statistiken für diese Auswahl gefunden.",
      });
      setStatus(matches.length ? "" : "Keine passenden Statistiken für diese Auswahl gefunden.");
      return;
    }
  };

  const handleAiSearch = async () => {
    // Filter zurücksetzen damit KI-Suche sichtbar wird
    setPreFilters({ themenfeld: "", indikator: "", datensatz: "" });
    startLoading("Claude durchsucht die Statistiken", "Eigene Daten und BFS STAT-TAB werden abgeglichen — beim ersten Mal kann das etwas dauern.");
    setStatus("Claude sucht passende Statistiken… (kann beim ersten Mal etwas dauern)");
    setSuggestions(null);
    try {
      const result = await api.smartSearch(query);
      if (!result.matches || !result.matches.length) {
        setStatus(result.erklaerung || "Keine passende Statistik gefunden. Versuch's mit anderen Begriffen oder nutze die erweiterte Suche.");
      } else {
        setSuggestions(result);
        setStatus(result.erklaerung || "Vorschläge gefunden — wähle eine Statistik.");
      }
    } catch (e) {
      setStatus("Fehler bei der Suche: " + e.message);
    }
    setLoading(false);
  };

  // Statistik ausgewählt -> direkt Daten laden und Ergebnis-Screen zeigen
  const selectStat = (match, gemeindeFromAi, aiFilter) => {
    // aiFilter kommt aus der KI-Suche wenn der Nutzer einen ganzen Satz eingegeben
    // hat ("Arbeitslosigkeit in Neuhausen von 2015 bis 2020"). Wir übernehmen
    // daraus Gemeinden, Zeitraum, Darstellungsmodus und Diagrammtyp.
    const f = aiFilter || {};
    const aiGemeinden = Array.isArray(f.gemeinden) && f.gemeinden.length
      ? f.gemeinden
      : (gemeindeFromAi ? [gemeindeFromAi] : []);

    setSelectedStat(match);
    // WICHTIG: setSuggestions(null) hier NICHT aufrufen — die Vorschlagsliste
    // bleibt im State erhalten, damit "Zurück zur Suche" wieder genau diese
    // Liste zeigt statt der leeren Startseite. Sie wird erst bei einer NEUEN
    // Suche (handleAiSearch/handleSimpleSearch) überschrieben.
    setRefineGemeinde(aiGemeinden);
    setRefineYearFrom(f.jahr_von ? String(f.jahr_von) : "");
    setRefineYearTo(f.jahr_bis ? String(f.jahr_bis) : "");
    setRefineFilterkategorie([]);
    setRefineFilterWert([]);
    setPendingAiTerms(Array.isArray(f.begriffe) ? f.begriffe : []);
    setRefineJahre([]);
    setIsMonthly(false);
    setAvailableFilters(null);
    setBfsDimensions([]);
    setBfsFilter1Code("");
    setBfsFilter1Values([]);
    setBfsFilter2Code("");
    setBfsFilter2Values([]);
    setGeoFilter({});
    setSdmxDimensions([]);
    setSdmxSelection({});
    setQuelleInfo(null);
    setDisplayMode(["original","indexiert","pct_yoy","abs_yoy"].includes(f.modus) ? f.modus : "original");
    setBaseYear(null);
    setChartType(f.diagramm === "saeule" ? "bar" : "line");
    setYMin("");
    setYMax("");
    setStep(STEP.RESULTS);

    // URL-Hash setzen damit Page-Reload die Statistik wiederherstellt
    try {
      window.location.hash = encodeURIComponent(JSON.stringify(match));
    } catch {}

    if (match.source === "bfs") {
      api.bfsFilterDimensions(match.bfs_table_id)
        .then((d) => setBfsDimensions(d.dimensions || []))
        .catch(() => setBfsDimensions([]));
      api.getQuelle(undefined, match.bfs_table_id).then(setQuelleInfo).catch(() => setQuelleInfo(null));
    } else if (match.source === "sdmx") {
      api.sdmxFilterDimensions(match.sdmx_id)
        .then((d) => {
          setSdmxDimensions(d.dimensions || []);
          setSdmxSelection(d.default_selection || {});
          if (d.quelle) setQuelleInfo({ herausgeber: d.quelle, titel: "" });
        })
        .catch(() => setSdmxDimensions([]));
    } else {
      api.getQuelle(match.datensatz, undefined).then(setQuelleInfo).catch(() => setQuelleInfo(null));
    }
  };

  // ---------- Schritt 2: Daten laden (initial + bei jeder Filteränderung) ----------

  // "Alle" (leeres Array, Ausgangszustand) -> Sentinel "__ALLE__" senden,
  // das Backend löst diesen zentral auf (alle Werte MINUS Total/Aggregat,
  // z.B. ohne "Schweiz") — robuster als eine Frontend-seitige Auflösung,
  // da hier nur eine Stelle (main.py: resolve_alle_sentinel) zuständig ist.
  // "__NONE__" (Nutzer hat im Excel-Style-Filter alles abgewählt) -> leeres
  // values-Array, das Backend liefert dann konsequent 0 Zeilen.
  const ALLE_SENTINEL = "__ALLE__";

  const resolveAllBfsFilter = useCallback((code, valuesRaw) => {
    if (!code) return null;
    if (valuesRaw && valuesRaw.length === 1 && valuesRaw[0] === "__NONE__") {
      return { code, values: [] };
    }
    if (valuesRaw && valuesRaw.length) return { code, values: valuesRaw };
    return { code, values: [ALLE_SENTINEL] };
  }, []);

  // Löst das "__NONE__"-Sentinel (Nutzer hat im Excel-Style-Filter alles
  // abgewählt) auf ein Array auf, das garantiert 0 Treffer ergibt — anders
  // als ein leeres Array, das beim Backend "kein Filter" (= alle) bedeutet.
  const resolveBaserowFilter = (values) => {
    if (values.length === 1 && values[0] === "__NONE__") return ["__KEIN_TREFFER__"];
    return values.length ? values : undefined;
  };

  const loadData = useCallback(async (stat, overrides) => {
    if (!stat) return;
    if (stat.source === "bfs") {
      startLoading("Daten vom Bundesamt für Statistik", "Die BFS STAT-TAB Schnittstelle wird live abgefragt.");
    } else {
      startLoading("Lade Daten aus der eigenen Datenbank", "Werte werden gefiltert und ins Wide-Format gebracht.");
    }
    setStatus("Lade Daten…");

    const gemeindeVal = overrides?.gemeinde !== undefined ? overrides.gemeinde : refineGemeinde;
    const yearFromVal = overrides?.yearFrom !== undefined ? overrides.yearFrom : refineYearFrom;
    const yearToVal = overrides?.yearTo !== undefined ? overrides.yearTo : refineYearTo;
    const filterkategorieVal = overrides?.filterkategorie !== undefined ? overrides.filterkategorie : refineFilterkategorie;
    const filterWertVal = overrides?.filterWert !== undefined ? overrides.filterWert : refineFilterWert;

    try {
      let data;
      if (stat.source === "bfs") {
        // Den Teil nach dem "—" aus bfs_title als bevorzugten Wert für
        // nicht-Zeit-Dimensionen verwenden (z.B. "Beschäftigte" statt "Total")
        const preferValue = stat.bfs_title && stat.bfs_title.includes("—")
          ? stat.bfs_title.split("—").pop().trim()
          : null;

        const f1Code = overrides?.filter1Code !== undefined ? overrides.filter1Code : bfsFilter1Code;
        const f1ValuesRaw = overrides?.filter1Values !== undefined ? overrides.filter1Values : bfsFilter1Values;
        const f2Code = overrides?.filter2Code !== undefined ? overrides.filter2Code : bfsFilter2Code;
        const f2ValuesRaw = overrides?.filter2Values !== undefined ? overrides.filter2Values : bfsFilter2Values;

        const f1 = resolveAllBfsFilter(f1Code, f1ValuesRaw);
        const f2 = resolveAllBfsFilter(f2Code, f2ValuesRaw);

        data = await api.bfsQuery(stat.bfs_table_id, undefined, preferValue, f1, f2);
      } else if (stat.source === "sdmx") {
        const sel = overrides?.sdmxSelection !== undefined ? overrides.sdmxSelection : sdmxSelection;
        data = await api.sdmxQuery(stat.sdmx_id, sel, yearFromVal || "", yearToVal || "");
        if (data.quelle) setQuelleInfo({ herausgeber: data.quelle, titel: data.title || "" });
      } else {
        const jahreVal = overrides?.jahre !== undefined ? overrides.jahre : refineJahre;
        // Monatsdaten: Jahr wird als filter_wert nicht genutzt — stattdessen
        // filtern wir Monatsdaten per Jahr direkt im Backend via filterkategorie=Monat
        // und senden die gewählten Jahre als filter_wert wenn isMonthly
        data = await api.search({
          themenfeld: stat.themenfeld,
          indikator: stat.indikator,
          datensatz: stat.datensatz,
          gemeinde: resolveBaserowFilter(gemeindeVal || []),
          filterkategorie: resolveBaserowFilter(filterkategorieVal || []),
          filter_wert: resolveBaserowFilter(filterWertVal || []),
        });
        setAvailableFilters(data.available_filters || null);
        setIsMonthly(data.is_monthly || false);

        // Stichwörter aus der KI-Suche auf die tatsächlich vorhandenen
        // Filterwerte abbilden (z.B. "Männer" -> Filterwert "Männer").
        if (pendingAiTerms.length && data.available_filters) {
          const alleWerte = data.available_filters.filter_values || [];
          const treffer = alleWerte.filter((w) =>
            pendingAiTerms.some((t) => fuzzyMatch(String(w), String(t)))
          );
          if (treffer.length) setRefineFilterWert(treffer);
          setPendingAiTerms([]);
        }
      }

      setAllYears(data.years);

      let years = data.years;
      let groups = data.groups;

      // Monatsdaten: Jahre-Filter — nur Gruppen (= Jahre) anzeigen die gewählt
      const jahreVal = overrides?.jahre !== undefined ? overrides.jahre : refineJahre;
      if (data.is_monthly && jahreVal && jahreVal.length && jahreVal[0] !== "__NONE__") {
        groups = groups.filter((g) => {
          // Datensatz-Label enthält das Jahr: "Arbeitslosenquote — 2023"
          return jahreVal.some((j) => g.Datensatz && g.Datensatz.includes(j));
        });
      }

      if (!yearFromVal && !yearToVal && !data.is_monthly) {
        // normal: no year filter
      } else if (!data.is_monthly && (yearFromVal || yearToVal)) {
        const from = yearFromVal ? parseInt(yearFromVal, 10) : -Infinity;
        const to = yearToVal ? parseInt(yearToVal, 10) : Infinity;
        years = years.filter((y) => {
          const yi = parseInt(y, 10);
          return yi >= from && yi <= to;
        });
      }

      if (!groups.length || !years.length) {
        setStatus("Keine Daten für diese Auswahl gefunden.");
        setResults(null);
      } else {
        setResults({ ...data, years, groups });
        setStatus(`${formatNumber(data.raw_count, 0)} Datenpunkte gefunden.`);
        // Basisjahr nur setzen wenn noch nicht gesetzt oder ausserhalb der verfügbaren Jahre
        setBaseYear((prev) => (!prev || !years.includes(String(prev))) ? years[0] : prev);
      }
    } catch (e) {
      setStatus("Fehler: " + e.message);
      setResults(null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initiales Laden, sobald eine Statistik gewählt wurde
  useEffect(() => {
    if (selectedStat && step === STEP.RESULTS) {
      loadData(selectedStat, { gemeinde: refineGemeinde });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStat]);

  // ----- Baserow-Filter Handler -----
  // Setzen nur den State — geladen wird erst per Klick auf "Anzeigen"
  // (siehe applyAllFilters), damit nicht bei jeder einzelnen Filteränderung
  // sofort eine neue Anfrage läuft.

  const applyGemeindeFilter = (values) => {
    setRefineGemeinde(values);
  };

  const applyYearFilter = (yearFrom, yearTo) => {
    setRefineYearFrom(yearFrom);
    setRefineYearTo(yearTo);
  };

  const applyFilterkategorie = (values) => {
    setRefineFilterkategorie(values);
    setRefineFilterWert([]); // Filter-Wert zurücksetzen, da abhängig von der Kategorie
  };

  const applyFilterWert = (values) => {
    setRefineFilterWert(values);
  };

  // ----- BFS-Filter Handler (Mehrfachauswahl) -----
  // Auch hier: nur State setzen, kein sofortiges Laden.

  const changeBfsFilter1Code = (code) => {
    setBfsFilter1Code(code);
    setBfsFilter1Values([]);
    if (bfsFilter2Code === code) {
      setBfsFilter2Code("");
      setBfsFilter2Values([]);
    }
  };

  const changeBfsFilter1Values = (values) => {
    setBfsFilter1Values(values);
  };

  const changeBfsFilter2Code = (code) => {
    setBfsFilter2Code(code);
    setBfsFilter2Values([]);
  };

  const changeBfsFilter2Values = (values) => {
    setBfsFilter2Values(values);
  };

  // Lädt die Daten mit dem AKTUELLEN Filter-State — wird vom "Anzeigen"-
  // Button ausgelöst, nachdem der Nutzer alle gewünschten Filter gesetzt hat.
  const applyAllFilters = () => {
    loadData(selectedStat, {
      gemeinde: refineGemeinde,
      yearFrom: refineYearFrom,
      yearTo: refineYearTo,
      filterkategorie: refineFilterkategorie,
      filterWert: refineFilterWert,
      jahre: refineJahre,
      filter1Code: bfsFilter1Code,
      filter1Values: bfsFilter1Values,
      filter2Code: bfsFilter2Code,
      filter2Values: bfsFilter2Values,
    });
  };

  // Setzt alle Filter auf den Ausgangszustand zurück (= Daten ungefiltert
  // wie direkt nach Auswahl der Statistik) — ohne dass die Seite neu
  // geladen werden muss.
  const resetAllFilters = () => {
    setRefineGemeinde([]);
    setRefineYearFrom("");
    setRefineYearTo("");
    setRefineFilterkategorie([]);
    setRefineFilterWert([]);
    setRefineJahre([]);
    setBfsFilter1Code("");
    setBfsFilter1Values([]);
    setBfsFilter2Code("");
    setBfsFilter2Values([]);
    setGeoFilter({});
    loadData(selectedStat, {
      gemeinde: [],
      yearFrom: "",
      yearTo: "",
      filterkategorie: [],
      filterWert: [],
      filter1Code: "",
      filter1Values: [],
      filter2Code: "",
      filter2Values: [],
    });
  };

  // ----- Export -----

  const handleExport = async () => {
    startLoading("Erstelle Excel-Export", "Die Tabelle wird im Wide-Format zusammengestellt.");
    setStatus("Erstelle Excel-Export…");
    try {
      if (selectedStat.source === "sdmx") {
        await api.exportExcelSdmx(selectedStat.sdmx_id, sdmxSelection, refineYearFrom || "", refineYearTo || "");
      } else if (selectedStat.source === "bfs") {
        const preferValue = selectedStat.bfs_title && selectedStat.bfs_title.includes("—")
          ? selectedStat.bfs_title.split("—").pop().trim()
          : null;
        await api.exportExcelBfs(
          selectedStat.bfs_table_id,
          preferValue,
          resolveAllBfsFilter(bfsFilter1Code, bfsFilter1Values),
          resolveAllBfsFilter(bfsFilter2Code, bfsFilter2Values),
        );
      } else {
        await api.exportExcel({
          themenfeld: selectedStat.themenfeld,
          indikator: selectedStat.indikator,
          datensatz: selectedStat.datensatz,
          gemeinde: resolveBaserowFilter(refineGemeinde),
          filterkategorie: resolveBaserowFilter(refineFilterkategorie),
          filter_wert: resolveBaserowFilter(refineFilterWert),
        });
      }
      setStatus("Export erfolgreich heruntergeladen.");
    } catch (e) {
      setStatus("Export-Fehler: " + e.message);
    }
    setLoading(false);
  };

  const handleExportImage = () => {
    if (!chartRef.current) return;
    const chartCanvas = chartRef.current.canvas;
    const title = selectedStat.source === "bfs"
      ? selectedStat.bfs_title
      : selectedStat.source === "sdmx"
      ? selectedStat.sdmx_title
      : `${selectedStat.indikator} — ${selectedStat.datensatz}`;
    const quelleText = quelleInfo?.herausgeber
      ? `Quelle: ${[quelleInfo.herausgeber, quelleInfo.titel].filter(Boolean).join(" — ")}`
      : "Quelle: WiFö Kanton Schaffhausen";
    const footerText = "WiFö Daten-Explorer — Wirtschaftsförderung Kanton Schaffhausen";

    const padding = 16;
    const titleH = 32;
    const footerH = 28;
    const offscreen = document.createElement("canvas");
    offscreen.width = chartCanvas.width;
    offscreen.height = chartCanvas.height + titleH + footerH;
    const ctx = offscreen.getContext("2d");

    // white background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, offscreen.width, offscreen.height);

    // title bar
    ctx.fillStyle = "#111111";
    ctx.fillRect(0, 0, offscreen.width, titleH);
    ctx.fillStyle = "#ffdd00";
    ctx.fillRect(0, titleH - 3, offscreen.width, 3);
    ctx.fillStyle = "white";
    ctx.font = "bold 13px sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(title, padding, titleH / 2);

    // chart
    ctx.drawImage(chartCanvas, 0, titleH);

    // footer
    const footerY = titleH + chartCanvas.height;
    ctx.fillStyle = "#f5f5f5";
    ctx.fillRect(0, footerY, offscreen.width, footerH);
    ctx.fillStyle = "#575757";
    ctx.font = "10px sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(quelleText, padding, footerY + footerH * 0.35);
    ctx.fillStyle = "#9d9d9d";
    ctx.font = "9px sans-serif";
    ctx.fillText(footerText, padding, footerY + footerH * 0.72);

    const url = offscreen.toDataURL("image/jpeg", 0.95);
    const a = document.createElement("a");
    a.href = url;
    const filenameBase = selectedStat.source === "bfs"
      ? selectedStat.bfs_table_id
      : `${selectedStat.indikator}_${selectedStat.datensatz}`.replace(/\s+/g, "_");
    a.download = `${filenameBase}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const backToSearch = () => {
    setStep(previousStep || STEP.SEARCH);
    setPreviousStep(STEP.SEARCH);
    setSelectedStat(null);
    setResults(null);
    setStatus("");
    window.location.hash = "";
  };

  const deletePayload = (d) => (
    d.source === "bfs"
      ? { source: "bfs", bfs_table_id: d.table_id }
      : d.source === "sdmx"
      ? { source: "sdmx", sdmx_id: d.id }
      : { source: "eigene_db", themenfeld: d.themenfeld, indikator: d.indikator, datensatz: d.datensatz }
  );

  const requestDelete = async (d) => {
    setEditingCard(null);
    setDeleteTarget(d);
    setDeleteInfo(null);
    setDeleteError("");
    try {
      setDeleteInfo(await api.datensatzDeletePreview(deletePayload(d)));
    } catch (e) {
      setDeleteError(e.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await api.datensatzDelete(deletePayload(deleteTarget));
      if (deleteTarget.source === "sdmx") {
        setSdmxStats(prev => prev.filter(s => s.id !== deleteTarget.id));
      } else {
        setAllDatensaetze(prev => prev.filter(x =>
          deleteTarget.source === "bfs"
            ? x.table_id !== deleteTarget.table_id
            : !(x.datensatz === deleteTarget.datensatz && x.indikator === deleteTarget.indikator)
        ));
      }
      setDeleteTarget(null);
      setDeleteInfo(null);
      setStatus("Statistik gelöscht.");
    } catch (e) {
      setDeleteError(e.message);
    }
    setDeleteBusy(false);
  };

  const saveCardPref = async (cardKey, prefs) => {
    const next = { ...cardPrefs, [cardKey]: { ...(cardPrefs[cardKey] || {}), ...prefs } };
    setCardPrefs(next);
    try { await api.setCardPref(cardKey, prefs); } catch {}
  };

  // ---------- Render ----------

  const hasGemeindeFilter = selectedStat?.source !== "bfs" && availableFilters?.gemeinden?.length > 1;
  const hasFilterkategorie = selectedStat?.source !== "bfs" && availableFilters?.filterkategorien?.length > 0;
  const hasJahrFilter = selectedStat?.source !== "bfs" && isMonthly && availableFilters?.jahre?.length > 0;

  return (
    <div className="page">
      {/* Login Screen — zeigen bis Auth gecheckt und User bekannt */}
      {authChecked && currentUser === false && (
        <div className="login-screen">
          <div className="login-card">
            <img src="/assets/logo.png" alt="Schaffhausen" className="login-logo" />
            <h1>WiFö Daten-Explorer</h1>
            <p>Melde dich mit deinem Schaffhausen-Konto an um fortzufahren.</p>
            {window.location.search.includes("auth_error") && (
              <div className="login-error">
                Anmeldung fehlgeschlagen: {new URLSearchParams(window.location.search).get("auth_error") || "Unbekannter Fehler"}
              </div>
            )}
            <a href="/auth/login" className="btn-ms-login">
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Mit Microsoft anmelden
            </a>
          </div>
        </div>
      )}

      {/* Ladescreen während Auth gecheckt wird */}
      {!authChecked && (
        <div className="login-screen">
          <div className="login-card">
            <div className="loading-spinner" style={{margin:"0 auto"}}/>
          </div>
        </div>
      )}

      {/* App nur anzeigen wenn eingeloggt */}
      {(!authChecked || currentUser) && (<>

      <header className="header">
        <img src="/assets/logo.png" alt="Schaffhausen – area for makers" className="header-logo" />
        <span className="badge">Daten-Explorer</span>
        <nav className="header-nav">
          <button
            className={`header-nav-link ${step === STEP.SEARCH || step === STEP.RESULTS ? "active" : ""}`}
            onClick={() => { setStep(STEP.SEARCH); setSelectedStat(null); setResults(null); setStatus(""); }}
          >
            Suche
          </button>
          <button
            className={`header-nav-link ${step === STEP.OVERVIEW ? "active" : ""}`}
            onClick={() => { setStep(STEP.OVERVIEW); setOverviewSearch(""); }}
          >
            Alle Statistiken
          </button>
          {chatbotEnabled && (
            <button
              className={`header-nav-link ${step === STEP.CHAT ? "active" : ""}`}
              onClick={() => setStep(STEP.CHAT)}
            >
              💬 Assistent
            </button>
          )}
          <button
            className="header-nav-link"
            onClick={() => setShowManual(true)}
          >
            ? Anleitung
          </button>
          {(myRole === "admin" || myRole === "editor") && (
            <button
              className={`header-nav-link ${step === STEP.IMPORT ? "active" : ""}`}
              onClick={() => setStep(STEP.IMPORT)}
            >
              ↑ Import
            </button>
          )}
          <button
            className={`header-nav-link ${step === STEP.ADMIN ? "active" : ""}`}
            onClick={() => setStep(STEP.ADMIN)}
          >
            ⚙ Admin
          </button>
          {currentUser && (
            <div className="header-user">
              <span className="header-user-name">{currentUser.name || currentUser.email}</span>
              <button className="header-user-logout" onClick={async () => {
                await api.logout();
                setCurrentUser(false);
              }}>Abmelden</button>
            </div>
          )}
        </nav>
      </header>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <div className="loading-spinner" />
            <div className="loading-text">{loadingTitle}</div>
            {loadingSubtext && <div className="loading-subtext">{loadingSubtext}</div>}
          </div>
        </div>
      )}

      {showManual && (
        <div className="manual-overlay" onClick={(e) => e.target === e.currentTarget && setShowManual(false)}>
          <div className="manual-modal">
            <div className="manual-header">
              <h2>Anleitung — Daten-Explorer</h2>
              <button className="manual-close" onClick={() => setShowManual(false)}>✕</button>
            </div>
            <div className="manual-body">

              <div className="manual-section">
                <div className="manual-step-num">1</div>
                <div>
                  <h3>Statistik suchen</h3>
                  <p>Gib einen Begriff oder eine <strong>ganze Frage</strong> ins Suchfeld ein und klick <strong>Suchen</strong>. Die KI findet passende Statistiken aus der eigenen Datenbank und dem BFS STAT-TAB — und liest dabei auch Gemeinde, Zeitraum und Darstellung aus der Frage heraus.</p>
                  <ul className="manual-list">
                    <li>«Wie hat sich die Arbeitslosigkeit in Neuhausen von 2015 bis 2020 entwickelt?» → öffnet die Statistik direkt mit Gemeinde Neuhausen und dem Zeitraum 2015–2020</li>
                    <li>«Beschäftigte in Schaffhausen letzte 5 Jahre als Säulendiagramm» → setzt Zeitraum und Diagrammtyp</li>
                    <li>«Bevölkerungsentwicklung prozentual zum Vorjahr» → aktiviert den Modus Δ% YoY</li>
                  </ul>
                  <p className="manual-tip">💡 Vor dem Öffnen zeigen gelbe Chips an, welche Filter erkannt wurden. In der Statistik lässt sich alles nachträglich anpassen. Tippfehler werden toleriert.</p>
                </div>
              </div>

              <div className="manual-section">
                <div className="manual-step-num">2</div>
                <div>
                  <h3>Filter Suche</h3>
                  <p>Alternativ zur KI-Suche über <strong>Themenfeld → Indikator → Datensatz</strong> filtern. Auch direkt beim Datensatz einsteigen möglich — andere Felder füllen sich automatisch.</p>
                  <p className="manual-tip">💡 BFS-Datensätze sind mit <span className="source-badge bfs">BFS</span> markiert.</p>
                </div>
              </div>

              <div className="manual-section">
                <div className="manual-step-num">3</div>
                <div>
                  <h3>Daten filtern &amp; anzeigen</h3>
                  <p>Filter setzen (Gemeinde, Jahr, Filterkategorie), dann <strong>Anzeigen</strong> klicken. Linie/Säule wechseln, Analyse-Modi nutzen (Indexiert, Δ% YoY, Δ abs. YoY), Y-Achse manuell begrenzen.</p>
                  <p className="manual-tip">💡 Excel-Export enthält Quellenblatt. JPEG-Export enthält Titel und Quelle als Fusszeile.</p>
                </div>
              </div>

              <div className="manual-section">
                <div className="manual-step-num">4</div>
                <div>
                  <h3>Daten-Assistent (Chat)</h3>
                  <p>Über <strong>💬 Assistent</strong> im Header oder den Chat-Button unten rechts Datenfragen stellen. Der Assistent antwortet mit Zahlen und zeigt den Graphen direkt im Chat — Klick auf <strong>📊 Vollbild</strong> öffnet die volle Statistik-Ansicht.</p>
                  <p className="manual-tip">💡 Chats werden pro User gespeichert und nach 30 Tagen Inaktivität automatisch gelöscht.</p>
                </div>
              </div>

              <div className="manual-section">
                <div className="manual-step-num">5</div>
                <div>
                  <h3>Daten importieren (Editor/Admin)</h3>
                  <p>Über <strong>↑ Import</strong> im Header stehen drei Import-Arten zur Verfügung:</p>
                  <ul className="manual-list">
                    <li><strong>📄 CSV / Eigene Daten</strong> — Vorlage herunterladen, ausfüllen, hochladen. Metadaten ergänzen, kontrollieren, importieren.</li>
                    <li><strong>🏛 BFS STAT-TAB</strong> — px-Tabellen-ID eingeben (z.B. px-x-0602010000_101), Vorschau, importieren.</li>
                    <li><strong>🌐 stats.swiss</strong> — Statistik live einbinden, ohne Datenimport (siehe unten).</li>
                  </ul>
                  <p className="manual-tip">💡 Bei allen: Duplikate werden automatisch übersprungen. Import-Protokoll &amp; Rückgängig-Funktion im Admin-Portal unter <strong>📥 Imports</strong>.</p>
                </div>
              </div>

              <div className="manual-section">
                <div className="manual-step-num">6</div>
                <div>
                  <h3>stats.swiss Statistik einbinden</h3>
                  <p>Statistiken aus dem <strong>Swiss Stats Explorer</strong> werden <strong>live abgefragt</strong> — es werden keine Daten importiert. Die App holt beim Öffnen genau die Werte die angezeigt werden, direkt von der offiziellen BFS-API. Dadurch sind die Daten immer aktuell und es gibt keine Grössenbeschränkung.</p>
                  <ul className="manual-list">
                    <li>Im Import-Tab <strong>🌐 stats.swiss</strong> die URL der Statistik einfügen (aus der Adressleiste auf stats.swiss oder aus «Entwickler-API»).</li>
                    <li>Themenfeld, Indikator und Datensatz-Name zuordnen — so wird die Statistik in der App gefunden.</li>
                    <li><strong>Standard-Auswahl</strong> festlegen: was beim Öffnen angezeigt wird (z.B. nur Schaffhausen). Nichts angekreuzt = alle Werte.</li>
                    <li>«Statistik einbinden» — sie erscheint sofort in Suche und Übersicht, mit dem Badge <span className="source-badge sdmx">stats.swiss</span>.</li>
                  </ul>
                  <p className="manual-tip">💡 In der Statistik-Ansicht können Nutzende die Filter jederzeit umstellen — die Daten werden dann neu abgefragt. Excel- und JPEG-Export funktionieren wie bei allen anderen Statistiken.</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      <div className="container">

        {step === STEP.SEARCH && (
          <>
            <div className="search-row">
              <div className="search-input-wrapper" ref={searchRef}>
                <input
                  className="search-input"
                  type="text"
                  placeholder="Begriff oder ganze Frage, z.B. «Wie hat sich die Arbeitslosigkeit in Neuhausen von 2015 bis 2020 entwickelt?»"
                  value={query}
                  onChange={(e) => {
                    const val = e.target.value;
                    setQuery(val);
                    if (val.trim().length >= 2) {
                      const indMatches = indikatorenList
                        .filter((v) => fuzzyMatch(v, val.trim()))
                        .map((v) => ({ label: v, type: "Indikator", stat: { source: "eigene_db", indikator: v } }));
                      const dsMatches = [...allDatensaetze, ...sdmxStats]
                        .filter((d) => d.datensatz && fuzzyMatch(d.datensatz, val.trim()))
                        .map((d) => ({
                          label: d.datensatz,
                          type: d.source === "bfs" ? "BFS" : d.source === "sdmx" ? "stats.swiss" : "Datensatz",
                          stat: d.source === "bfs"
                            ? { source: "bfs", bfs_table_id: d.table_id, bfs_title: `${d.indikator} — ${d.datensatz}` }
                            : d.source === "sdmx"
                            ? { source: "sdmx", sdmx_id: d.id, sdmx_title: `${d.indikator} — ${d.datensatz}`, themenfeld: d.themenfeld, indikator: d.indikator, datensatz: d.datensatz, explorer_url: d.explorer_url }
                            : { source: "eigene_db", themenfeld: d.themenfeld, indikator: d.indikator, datensatz: d.datensatz },
                        }));
                      const seen = new Set();
                      const combined = [...dsMatches, ...indMatches].filter((h) => {
                        if (seen.has(h.label)) return false;
                        seen.add(h.label);
                        return true;
                      }).slice(0, 8);
                      setQueryHints(combined);
                      setShowHints(combined.length > 0);
                    } else {
                      setQueryHints([]);
                      setShowHints(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { setShowHints(false); handleSearch(); }
                    if (e.key === "Escape") setShowHints(false);
                  }}
                  onFocus={() => queryHints.length > 0 && setShowHints(true)}
                />
                {showHints && (
                  <div className="search-hints">
                    {queryHints.map((h, i) => (
                      <div
                        key={i}
                        className="search-hint-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setShowHints(false);
                          setQueryHints([]);
                          if (h.stat && (h.stat.datensatz || h.stat.source === "bfs")) {
                            // Datensatz oder BFS: direkt zur Statistik navigieren
                            setQuery(h.label);
                            selectStat(h.stat);
                          } else {
                            // Nur Indikator: KI-Suche mit diesem Begriff starten
                            setQuery(h.label);
                            startLoading("Claude durchsucht die Statistiken", "Eigene Daten und BFS STAT-TAB werden abgeglichen.");
                            setSuggestions(null);
                            api.smartSearch(h.label).then((result) => {
                              if (result.matches?.length) {
                                setSuggestions(result);
                              }
                              setLoading(false);
                            }).catch(() => setLoading(false));
                          }
                        }}
                      >
                        <span className="search-hint-label">{h.label}</span>
                        <span className={`search-hint-type ${h.type === "BFS" ? "bfs" : ""}`}>{h.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="btn-primary" onClick={handleSearch} disabled={loading}>
                Suchen
              </button>
              <HelpTip text="Die KI-Suche versteht natürliche Sprache — schreib einfach was du suchst, z.B. 'Arbeitslosigkeit Neuhausen' oder 'Steuereinnahmen'. Vorschläge erscheinen ab 2 Zeichen, Tippfehler werden toleriert." />
            </div>

            <div className="advanced-panel">
                <div className="advanced-panel-fields">
                  <SearchableSelect
                    label="Themenfeld"
                    value={preFilters.themenfeld}
                    options={themenfelder.map((v) => ({ value: v, label: v }))}
                    onChange={(v) => updatePreFilter("themenfeld", v)}
                  />
                  <SearchableSelect
                    label="Indikator"
                    value={preFilters.indikator}
                    options={indikatorenList.map((v) => ({ value: v, label: v }))}
                    onChange={(v) => updatePreFilter("indikator", v)}
                  />
                  <SearchableSelect
                    label="Datensatz"
                    value={preFilters.datensatz}
                    options={datensaetzeList.map((d) => ({
                      value: d.datensatz,
                      label: `${d.datensatz}${d.source === "bfs" ? " (BFS)" : ""}`,
                    }))}
                    onChange={chooseDatensatzDirectly}
                  />
                </div>
                <div className="advanced-panel-actions">
                  {(preFilters.themenfeld || preFilters.indikator || preFilters.datensatz) && (
                    <button type="button" className="btn-secondary" onClick={resetPreFilters}>
                      ✕ Filter zurücksetzen
                    </button>
                  )}
                  <HelpTip text="Die Filter Suche erlaubt gezielte Auswahl: wähle erst ein Themenfeld, dann einen Indikator, dann einen Datensatz — oder starte direkt beim Datensatz (die anderen Felder werden automatisch ausgefüllt). Einzelne Felder können leer gelassen werden." />
                  <button
                    className="btn-primary"
                    onClick={handleSearch}
                    disabled={loading || !(preFilters.themenfeld || preFilters.indikator || preFilters.datensatz)}
                  >
                    Statistik wählen
                  </button>
                </div>
            </div>

            {status && <div className="status">{status}</div>}

            {suggestions && suggestions.matches && suggestions.matches.length > 0 && (
              <div className="suggestions">
                <h3>Statistik auswählen</h3>
                {(() => {
                  const f = suggestions.filter || {};
                  const chips = [];
                  const gem = (f.gemeinden && f.gemeinden.length) ? f.gemeinden : (suggestions.gemeinde ? [suggestions.gemeinde] : []);
                  if (gem.length) chips.push(gem.join(", "));
                  if (f.jahr_von || f.jahr_bis) {
                    chips.push(f.jahr_von && f.jahr_bis && f.jahr_von !== f.jahr_bis
                      ? `${f.jahr_von}–${f.jahr_bis}`
                      : String(f.jahr_von || f.jahr_bis));
                  }
                  if (f.begriffe && f.begriffe.length) chips.push(f.begriffe.join(", "));
                  const modusLabel = { indexiert: "Indexiert", pct_yoy: "Δ% YoY", abs_yoy: "Δ abs. YoY" }[f.modus];
                  if (modusLabel) chips.push(modusLabel);
                  if (f.diagramm === "saeule") chips.push("Säulendiagramm");
                  if (!chips.length) return null;
                  return (
                    <div className="ai-filter-hint">
                      <span className="ai-filter-hint-label">Wird direkt angewendet:</span>
                      {chips.map((ch, i) => <span key={i} className="ai-filter-chip">{ch}</span>)}
                    </div>
                  );
                })()}
                {suggestions.matches.map((m, i) => (
                  <div
                    key={i}
                    className={`suggestion-card ${m.empfohlen ? "recommended" : ""}`}
                    onClick={() => selectStat(m, suggestions.gemeinde, suggestions.filter)}
                  >
                    <div className="suggestion-main">
                      {m.empfohlen && <span className="rec-badge">Empfohlen</span>}
                      {(() => {
                        const cardKey = m.source === "bfs" ? m.bfs_table_id : m.source === "sdmx" ? m.sdmx_id : `${m.themenfeld}__${m.indikator}__${m.datensatz}`;
                        const prefs = cardPrefs[cardKey] || {};
                        return (
                          <span className={`source-badge ${m.source}`}
                            style={prefs.sourceColor ? {background:prefs.sourceColor,color:"#111"} : {}}>
                            {prefs.sourceText ?? (m.source === "bfs" ? "BFS" : m.source === "sdmx" ? "stats.swiss" : "Eigene Daten")}
                          </span>
                        );
                      })()}
                      <strong>
                        {m.source === "bfs" ? m.bfs_title : m.source === "sdmx" ? m.sdmx_title : `${m.indikator} — ${m.datensatz}`}
                      </strong>
                    </div>
                    <div className="suggestion-meta">
                      {m.source === "bfs" ? m.bfs_table_id : m.themenfeld}
                      {m.confidence ? ` · Relevanz: ${m.confidence}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === STEP.RESULTS && selectedStat && (
          <div className="results">
            <button className="btn-link" onClick={backToSearch}>← Zurück {previousStep === STEP.CHAT ? "zum Chat" : "zur Suche"}</button>

            <div className="result-header">
              <h2 className="refine-title">
                {(() => {
                  const cardKey = selectedStat.source === "bfs"
                    ? selectedStat.bfs_table_id
                    : selectedStat.source === "sdmx"
                    ? selectedStat.sdmx_id
                    : `${selectedStat.themenfeld}__${selectedStat.indikator}__${selectedStat.datensatz}`;
                  const prefs = cardPrefs[cardKey] || {};
                  return (
                    <span className={`source-badge ${selectedStat.source}`}
                      style={prefs.sourceColor ? {background:prefs.sourceColor,color:"#111"} : {}}>
                      {prefs.sourceText ?? (selectedStat.source === "bfs" ? "BFS" : selectedStat.source === "sdmx" ? "stats.swiss" : "Eigene Daten")}
                    </span>
                  );
                })()}
                {selectedStat.source === "bfs" ? selectedStat.bfs_title : selectedStat.source === "sdmx" ? selectedStat.sdmx_title : `${selectedStat.indikator} — ${selectedStat.datensatz}`}
              </h2>

              {quelleInfo && (quelleInfo.herausgeber || quelleInfo.titel) && (
                <div className="quelle-info">
                  Quelle: {[quelleInfo.herausgeber, quelleInfo.titel].filter(Boolean).join(" — ")}
                  {quelleInfo.link && (
                    /^https?:\/\//i.test(quelleInfo.link.trim()) ? (
                      <> · <a href={quelleInfo.link.trim()} target="_blank" rel="noopener noreferrer">Link</a></>
                    ) : (
                      <> · {quelleInfo.link.trim()}</>
                    )
                  )}
                </div>
              )}

              {/* Baserow-Filter: Gemeinde, Jahr, Filterkategorie/Filter — nur was für DIESE Statistik Sinn macht */}
              {(hasGemeindeFilter || allYears.length > 0 || hasFilterkategorie || hasJahrFilter) && (
                <div className="filter-section">
                  <div className="filter-section-label">Filter <HelpTip text="Diese Filter schränken die angezeigten Daten ein. Wähle Gemeinde/Kanton, Zeitraum oder Filterkategorien (z.B. Gewinnstufe). Klick auf 'Anzeigen' übernimmt alle Filter auf einmal." /></div>
                  <div className="inline-filters">
                    {hasGemeindeFilter && (
                    <CheckboxFilterDropdown
                      label="Gemeinde"
                      options={availableFilters.gemeinden.map((v) => ({ value: v, label: v }))}
                      selected={refineGemeinde}
                      onChange={applyGemeindeFilter}
                    />
                  )}
                  {hasJahrFilter && (
                    <CheckboxFilterDropdown
                      label="Jahr"
                      options={availableFilters.jahre.map((v) => ({ value: v, label: v }))}
                      selected={refineJahre}
                      onChange={(vals) => setRefineJahre(vals)}
                    />
                  )}
                  {hasFilterkategorie && (
                    <div className="field">
                      <label>Filterkategorie</label>
                      <select
                        value={refineFilterkategorie[0] || ""}
                        onChange={(e) => applyFilterkategorie(e.target.value ? [e.target.value] : [])}
                      >
                        <option value="">Keine Einschränkung</option>
                        {availableFilters.filterkategorien.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  )}
                  {hasFilterkategorie && refineFilterkategorie.length > 0 && availableFilters.filter_values.length > 0 && (
                    <CheckboxFilterDropdown
                      label={refineFilterkategorie.join(" / ")}
                      options={availableFilters.filter_values.map((v) => ({ value: v, label: v }))}
                      selected={refineFilterWert}
                      onChange={applyFilterWert}
                    />
                  )}
                  {!isMonthly && allYears.length > 0 && (
                    <>
                      <div className="field">
                        <label>Jahr von <HelpTip text="Nur Daten ab diesem Jahr anzeigen." /></label>
                        <select
                          value={refineYearFrom || allYears[0]}
                          onChange={(e) => setRefineYearFrom(e.target.value)}
                        >
                          {allYears.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      <div className="field">
                        <label>Jahr bis <HelpTip text="Nur Daten bis zu diesem Jahr anzeigen." /></label>
                        <select
                          value={refineYearTo || allYears[allYears.length - 1]}
                          onChange={(e) => setRefineYearTo(e.target.value)}
                        >
                          {allYears.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  </div>
                </div>
              )}

              {/* stats.swiss: Live-Filter je Dimension */}
              {selectedStat.source === "sdmx" && sdmxDimensions.length > 0 && (
                <div className="filter-section">
                  <div className="filter-section-label">
                    Filter <HelpTip text="Die Daten werden live von stats.swiss geholt. Nichts angekreuzt = alle Werte dieser Dimension. Bei sehr vielen Werten (z.B. alle Gemeinden) dauert die Abfrage länger — dann gezielt auswählen." />
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {sdmxDimensions.map(d => (
                      <SdmxDimensionPicker key={d.id} dim={d}
                        selected={sdmxSelection[d.id] || []}
                        onToggle={(code) => setSdmxSelection(s => {
                          const cur = s[d.id] || [];
                          const next = cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code];
                          const out = { ...s };
                          if (next.length) out[d.id] = next; else delete out[d.id];
                          return out;
                        })}
                        onClear={() => setSdmxSelection(s => { const o = { ...s }; delete o[d.id]; return o; })}
                      />
                    ))}
                  </div>
                  {selectedStat.explorer_url && (
                    <div style={{marginTop:10}}>
                      <a href={selectedStat.explorer_url} target="_blank" rel="noopener noreferrer"
                         style={{fontSize:"0.78rem",color:"var(--gray-700)"}}>
                        Statistik auf stats.swiss öffnen ↗
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* BFS-spezifisch: zwei kaskadierende Filter mit Excel-artiger Checkbox-Mehrfachauswahl */}
              {selectedStat.source === "bfs" && bfsDimensions.length > 0 && (
                <div className="filter-section">
                  <div className="filter-section-label">Filter <HelpTip text="BFS-Statistiken können nach verschiedenen Dimensionen gefiltert werden, z.B. Kanton oder Wirtschaftsabteilung. Filter 1 wählt die Dimension, dann erscheinen die Werte zur Auswahl. 'Alle' zeigt eine Linie pro Wert, Schweiz/Total wird dabei automatisch ausgeschlossen." /></div>
                  <div className="inline-filters bfs-filters">
                  <div className="field">
                    <label>Filter 1 <HelpTip text="Wähle zuerst eine Dimension (z.B. 'Kanton'), dann erscheint ein Checkbox-Menü mit allen verfügbaren Werten. Mehrfachauswahl möglich." /></label>
                    <select value={bfsFilter1Code} onChange={(e) => changeBfsFilter1Code(e.target.value)}>
                      <option value="">Keine Einschränkung</option>
                      {/* Geo-Dimensionen nur einmal anzeigen (ohne Duplikat Kanton/Bezirk/Gemeinde) */}
                      {(() => {
                        const seen = new Set();
                        return bfsDimensions.map((d) => {
                          if (seen.has(d.code)) return null;
                          seen.add(d.code);
                          // Geo-Dimension: zusammenfassen als einen Eintrag
                          const geoSiblings = bfsDimensions.filter(x => x.code === d.code && x.geo_level);
                          const label = geoSiblings.length > 1
                            ? geoSiblings.map(g => g.text).join(" / ")
                            : d.text;
                          return <option key={d.code} value={d.code}>{label}</option>;
                        });
                      })()}
                    </select>
                  </div>

                  {bfsFilter1Code && (() => {
                    const geoSiblings = bfsDimensions.filter(d => d.code === bfsFilter1Code && d.geo_level);
                    if (geoSiblings.length > 1) {
                      // Geo-Hierarchie: 3 separate Dropdowns
                      return geoSiblings.map((geo) => (
                        <CheckboxFilterDropdown
                          key={geo.geo_level}
                          label={geo.text}
                          options={geo.options}
                          selected={geoFilter[geo.geo_level] || []}
                          onChange={(vals) => {
                            const next = { ...geoFilter, [geo.geo_level]: vals };
                            setGeoFilter(next);
                            // Kombiniere alle gewählten Geo-Werte als filter1Values
                            const combined = geoSiblings.flatMap(g => {
                              const sel = g.geo_level === geo.geo_level ? vals : (next[g.geo_level] || []);
                              const isNone = sel.length === 1 && sel[0] === "__NONE__";
                              const isAll = sel.length === 0;
                              if (isNone || isAll) return [];
                              return sel;
                            });
                            setBfsFilter1Values(combined.length ? combined : []);
                          }}
                        />
                      ));
                    }
                    // Normale Dimension: ein Dropdown
                    return (
                      <CheckboxFilterDropdown
                        label={bfsDimensions.find((d) => d.code === bfsFilter1Code)?.text || "Werte"}
                        options={bfsDimensions.find((d) => d.code === bfsFilter1Code)?.options || []}
                        selected={bfsFilter1Values}
                        onChange={changeBfsFilter1Values}
                      />
                    );
                  })()}

                  {bfsFilter1Code && (
                    <div className="field">
                      <label>Filter 2 (optional)</label>
                      <select value={bfsFilter2Code} onChange={(e) => changeBfsFilter2Code(e.target.value)}>
                        <option value="">Keine Einschränkung</option>
                        {(() => {
                          const seen = new Set();
                          seen.add(bfsFilter1Code); // exclude filter1 code entirely
                          return bfsDimensions
                            .filter(d => d.code !== bfsFilter1Code && !seen.has(d.code))
                            .map(d => { seen.add(d.code); return <option key={d.code} value={d.code}>{d.text}</option>; });
                        })()}
                      </select>
                    </div>
                  )}
                  {bfsFilter2Code && (
                    <CheckboxFilterDropdown
                      label={bfsDimensions.find((d) => d.code === bfsFilter2Code)?.text || "Werte"}
                      options={bfsDimensions.find((d) => d.code === bfsFilter2Code)?.options || []}
                      selected={bfsFilter2Values}
                      onChange={changeBfsFilter2Values}
                    />
                  )}
                  </div>
                </div>
              )}

              {/* Filter werden erst beim Klick übernommen — kein Reload bei jeder einzelnen Änderung */}
              {(hasGemeindeFilter || allYears.length > 0 || hasFilterkategorie || hasJahrFilter || (selectedStat.source === "bfs" && bfsDimensions.length > 0) || (selectedStat.source === "sdmx" && sdmxDimensions.length > 0)) && (
                <div className="filter-apply-row">
                  <button className="btn-secondary" onClick={resetAllFilters} disabled={loading}>
                    Filter zurücksetzen
                  </button>
                  <button className="btn-primary" onClick={applyAllFilters} disabled={loading}>
                    Anzeigen
                  </button>
                </div>
              )}
            </div>

            {status && <div className="status">{status}</div>}

            {results && (() => {
              const effectiveBaseYear = String(baseYear || results.years[0]);
              const displayResults = applyMode(results, displayMode, effectiveBaseYear);
              const yLabel = displayMode === "index"
                ? `Index (${effectiveBaseYear} = 100)`
                : displayMode === "rel_yoy" ? "Veränderung % (YoY)"
                : displayMode === "abs_yoy" ? "Absolute Veränderung (YoY)"
                : "";
              return (
              <>
                {/* Modus-Umschalter */}
                <div className="mode-switcher">
                  <div className="mode-tabs">
                    {MODES.map((m) => (
                      <button
                        key={m.id}
                        className={`mode-tab ${displayMode === m.id ? "active" : ""}`}
                        onClick={() => setDisplayMode(m.id)}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <HelpTip text="Original: Rohwerte. Indexiert: Alle Werte relativ zum Basisjahr (= 100), gut für Vergleiche mit unterschiedlichen Grössenordnungen. Δ% YoY: Prozentuale Veränderung zum Vorjahr. Δ abs. YoY: Absolute Veränderung zum Vorjahr." />
                  {displayMode === "index" && (
                    <div className="mode-option">
                      <label>Basisjahr <HelpTip text="Das Referenzjahr erhält den Wert 100. Alle anderen Jahre werden relativ dazu dargestellt — gut um Entwicklungen unabhängig von der absoluten Grösse zu vergleichen." /></label>
                      <select
                        value={effectiveBaseYear}
                        onChange={(e) => setBaseYear(e.target.value)}
                      >
                        {results.years.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="mode-tabs chart-type-tabs">
                    <button
                      className={`mode-tab ${chartType === "line" ? "active" : ""}`}
                      onClick={() => setChartType("line")}
                      title="Liniendiagramm"
                    >〜 Linie</button>
                    <button
                      className={`mode-tab ${chartType === "bar" ? "active" : ""}`}
                      onClick={() => setChartType("bar")}
                      title="Säulendiagramm"
                    >▐ Säule</button>
                  </div>
                  <div className="mode-option">
                    <label>Y Min <HelpTip text="Minimaler Y-Achsen-Wert. Leer = automatisch." /></label>
                    <input type="number" value={yMin} onChange={(e) => setYMin(e.target.value)} placeholder="Auto" className="y-axis-input" />
                  </div>
                  <div className="mode-option">
                    <label>Y Max <HelpTip text="Maximaler Y-Achsen-Wert. Leer = automatisch." /></label>
                    <input type="number" value={yMax} onChange={(e) => setYMax(e.target.value)} placeholder="Auto" className="y-axis-input" />
                  </div>
                </div>

                <div className="chart-box">
                  <div className="chart-header">
                    <button className="btn-export" onClick={handleExportImage}>
                      ⬇ Grafik als JPEG
                    </button>
                  </div>
                  {chartType === "line" ? (
                  <Line
                    key={`line-${yMin}-${yMax}-${displayMode}-${effectiveBaseYear}`}
                    ref={chartRef}
                    plugins={[{
                      id: "white_background",
                      beforeDraw: (chart) => {
                        const { ctx } = chart;
                        ctx.save();
                        ctx.globalCompositeOperation = "destination-over";
                        ctx.fillStyle = "white";
                        ctx.fillRect(0, 0, chart.width, chart.height);
                        ctx.restore();
                      },
                    }, displayMode === "index" ? {
                      id: "index_year_line",
                      afterDraw: (chart) => {
                        const baseYearStr = String(effectiveBaseYear);
                        const idx = chart.data.labels.findIndex(l => String(l) === baseYearStr);
                        if (idx < 0) return;
                        const meta = chart.getDatasetMeta(0);
                        if (!meta || !meta.data[idx]) return;
                        const x = meta.data[idx].x;
                        const { ctx, chartArea: { top, bottom } } = chart;
                        ctx.save();
                        // yellow dashed line
                        ctx.beginPath();
                        ctx.setLineDash([5, 4]);
                        ctx.strokeStyle = "#ffdd00";
                        ctx.lineWidth = 3;
                        ctx.moveTo(x, top);
                        ctx.lineTo(x, bottom);
                        ctx.stroke();
                        ctx.setLineDash([]);
                        // label pill below top
                        const label = `Basis ${baseYearStr}`;
                        ctx.font = "bold 11px Manrope, sans-serif";
                        const tw = ctx.measureText(label).width;
                        const pad = 6;
                        ctx.fillStyle = "#111111";
                        ctx.beginPath();
                        ctx.roundRect(x - tw/2 - pad, top + 6, tw + pad*2, 20, 4);
                        ctx.fill();
                        ctx.fillStyle = "#ffdd00";
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillText(label, x, top + 16);
                        ctx.restore();
                      },
                    } : null].filter(Boolean)}
                    data={{
                      labels: displayResults.years,
                      datasets: displayResults.groups.map((g, i) => ({
                        label: [g.Gemeinde, g.Datensatz].filter(Boolean).join(" — "),
                        data: displayResults.years.map((y) => g.values[y] ?? null),
                        borderColor: COLORS[i % COLORS.length],
                        backgroundColor: COLORS[i % COLORS.length] + "20",
                        borderWidth: 2,
                        pointRadius: 4,
                        tension: 0.3,
                        spanGaps: true,
                      })),
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "bottom" },
                        tooltip: {
                          mode: "point", intersect: true,
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y)}`,
                          },
                        },
                      },
                      scales: {
                        y: { beginAtZero: !yMin && (displayMode === "original" || displayMode === "abs_yoy"), min: yMin !== "" ? parseFloat(yMin) : undefined, max: yMax !== "" ? parseFloat(yMax) : undefined, grid: { color: "#f3f4f6" }, ticks: { callback: (v) => formatNumber(v) }, title: yLabel ? { display: true, text: yLabel, font: { size: 11 } } : undefined },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                  ) : (
                  <Bar
                    key={`bar-${yMin}-${yMax}-${displayMode}-${effectiveBaseYear}`}
                    ref={chartRef}
                    plugins={[{
                      id: "white_background",
                      beforeDraw: (chart) => {
                        const { ctx } = chart;
                        ctx.save();
                        ctx.globalCompositeOperation = "destination-over";
                        ctx.fillStyle = "white";
                        ctx.fillRect(0, 0, chart.width, chart.height);
                        ctx.restore();
                      },
                    }]}
                    data={{
                      labels: displayResults.years,
                      datasets: displayResults.groups.map((g, i) => ({
                        label: [g.Gemeinde, g.Datensatz].filter(Boolean).join(" — "),
                        data: displayResults.years.map((y) => g.values[y] ?? null),
                        backgroundColor: COLORS[i % COLORS.length] + "cc",
                        borderColor: COLORS[i % COLORS.length],
                        borderWidth: 1,
                        borderRadius: 4,
                      })),
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "bottom" },
                        tooltip: {
                          mode: "point", intersect: true,
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y)}`,
                          },
                        },
                      },
                      scales: {
                        y: { beginAtZero: !yMin, min: yMin !== "" ? parseFloat(yMin) : undefined, max: yMax !== "" ? parseFloat(yMax) : undefined, grid: { color: "#f3f4f6" }, ticks: { callback: (v) => formatNumber(v) }, title: yLabel ? { display: true, text: yLabel, font: { size: 11 } } : undefined },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                  )}
                </div>

                <div className="table-box">
                  <div className="table-header">
                    <h3>Daten im Wide Format{yLabel ? ` — ${yLabel}` : ""}</h3>
                    <button className="btn-export" onClick={handleExport}>
                      ⬇ Excel exportieren
                    </button>
                  </div>
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          {selectedStat.source !== "bfs" && <th>Gemeinde</th>}
                          {selectedStat.source !== "bfs" && <th>Themenfeld</th>}
                          <th>Indikator</th>
                          <th>Datensatz</th>
                          {displayResults.years.map((y) => <th key={y}>{y}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {displayResults.groups.map((g, i) => (
                          <tr key={i}>
                            {selectedStat.source !== "bfs" && <td>{g.Gemeinde}</td>}
                            {selectedStat.source !== "bfs" && <td>{g.Themenfeld}</td>}
                            <td>{g.Indikator}</td>
                            <td>{g.Datensatz}</td>
                            {displayResults.years.map((y) => (
                              <td key={y}>{g.values[y] !== undefined && g.values[y] !== null ? formatNumber(g.values[y]) : "—"}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
              );
            })()}

          </div>
        )}

        {/* Übersichtsseite: alle Statistiken als kompakte, klickbare Kacheln */}
        {step === STEP.OVERVIEW && (
          <div className="overview">
            <div className="overview-header">
              <h2 className="overview-title">Alle Statistiken</h2>
              <input
                className="overview-search"
                type="text"
                placeholder="Suchen…"
                value={overviewSearch}
                onChange={(e) => setOverviewSearch(e.target.value)}
              />
            </div>
            <div className="overview-grid">
              {[...allDatensaetze, ...sdmxStats]
                .filter((d) => !overviewSearch.trim() || fuzzyMatch(d.datensatz || "", overviewSearch.trim()) || fuzzyMatch(d.indikator || "", overviewSearch.trim()) || fuzzyMatch(d.themenfeld || "", overviewSearch.trim()))
                .sort((a, b) => (a.datensatz || "").localeCompare(b.datensatz || "", "de"))
                .map((d, i) => {
                  const cardKey = d.source === "bfs" ? d.table_id : d.source === "sdmx" ? d.id : `${d.themenfeld}__${d.indikator}__${d.datensatz}`;
                  const prefs = cardPrefs[cardKey] || {};
                  const isEditing = editingCard === cardKey;
                  const sourceBadgeText = prefs.sourceText ?? (d.source === "bfs" ? "BFS" : d.source === "sdmx" ? "stats.swiss" : "Eigene");
                  const sourceBadgeColor = prefs.sourceColor || (d.source === "sdmx" ? "#fce7f3" : null);
                  return (
                  <div key={i} className="overview-card">
                    <div className="overview-card-top">
                      <div className="overview-card-badges">
                        {d.themenfeld && (
                          <span className="overview-card-thema" style={prefs.themaColor ? {background:prefs.themaColor} : {}}>
                            {prefs.themaText ?? d.themenfeld}
                          </span>
                        )}
                        <span className={`source-badge ${d.source}`} style={sourceBadgeColor ? {background:sourceBadgeColor,color:"#111"} : {}}>
                          {sourceBadgeText}
                        </span>
                      </div>
                      {(myRole === "admin" || myRole === "editor") && (
                        <button className="overview-card-gear" onClick={e => { e.stopPropagation(); setEditingCard(isEditing ? null : cardKey); }} title="Badge-Text und Farbe anpassen (nur für Admins/Editoren)">⚙</button>
                      )}
                    </div>
                    {isEditing && (myRole === "admin" || myRole === "editor") ? (
                      <div className="overview-card-settings" onClick={e => e.stopPropagation()}>
                        <div style={{fontSize:"0.76rem",fontWeight:700,marginBottom:8,color:"var(--gray-700)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Badge anpassen</div>

                        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
                          <div style={{fontSize:"0.74rem",fontWeight:600,color:"var(--gray-700)"}}>Themenfeld-Badge</div>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input
                              value={prefs.themaText ?? (d.themenfeld || "")}
                              onChange={e => saveCardPref(cardKey, {themaText: e.target.value})}
                              placeholder="Text"
                              style={{flex:1,padding:"4px 8px",borderRadius:6,border:"1px solid rgba(17,17,17,0.15)",fontSize:"0.78rem",fontFamily:"inherit"}}
                            />
                            <input type="color" value={prefs.themaColor || "#e0f2fe"}
                              onChange={e => saveCardPref(cardKey, {themaColor: e.target.value})}
                              style={{width:28,height:28,border:"none",borderRadius:6,cursor:"pointer",padding:2}} />
                          </div>

                          <div style={{fontSize:"0.74rem",fontWeight:600,color:"var(--gray-700)"}}>Quellen-Badge</div>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input
                              value={prefs.sourceText ?? sourceBadgeText}
                              onChange={e => saveCardPref(cardKey, {sourceText: e.target.value})}
                              placeholder="Text"
                              style={{flex:1,padding:"4px 8px",borderRadius:6,border:"1px solid rgba(17,17,17,0.15)",fontSize:"0.78rem",fontFamily:"inherit"}}
                            />
                            <input type="color" value={prefs.sourceColor || (d.source === "bfs" ? "#fef3c7" : d.source === "sdmx" ? "#fce7f3" : "#ccfbf1")}
                              onChange={e => saveCardPref(cardKey, {sourceColor: e.target.value})}
                              style={{width:28,height:28,border:"none",borderRadius:6,cursor:"pointer",padding:2}} />
                          </div>
                        </div>

                        <div style={{display:"flex",gap:6}}>
                          <button className="btn-secondary" style={{fontSize:"0.74rem",padding:"4px 10px"}}
                            onClick={() => { saveCardPref(cardKey, {themaText:null,themaColor:null,sourceText:null,sourceColor:null}); }}>
                            Zurücksetzen
                          </button>
                          <button className="btn-primary" style={{fontSize:"0.76rem",padding:"5px 12px"}}
                            onClick={() => setEditingCard(null)}>
                            Fertig ✓
                          </button>
                        </div>

                        <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid rgba(239,68,68,0.2)"}}>
                          <button
                            style={{background:"none",border:"none",cursor:"pointer",color:"#ef4444",fontSize:"0.76rem",fontWeight:600,fontFamily:"inherit",padding:0}}
                            onClick={() => requestDelete(d)}>
                            🗑 Statistik löschen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div onClick={() => { selectStat(d.source === "bfs" ? {source:"bfs",bfs_table_id:d.table_id,bfs_title:`${d.indikator} — ${d.datensatz}`} : d.source === "sdmx" ? {source:"sdmx",sdmx_id:d.id,sdmx_title:`${d.indikator} — ${d.datensatz}`,themenfeld:d.themenfeld,indikator:d.indikator,datensatz:d.datensatz} : {source:"eigene_db",themenfeld:d.themenfeld,indikator:d.indikator,datensatz:d.datensatz}); }} style={{cursor:"pointer"}}>
                        <div className="overview-card-title">{d.datensatz}</div>
                        {d.indikator && <div className="overview-card-indikator">{d.indikator}</div>}
                      </div>
                    )}
                  </div>
                  );
                })}
            </div>
          </div>
        )}

        {step === STEP.IMPORT && (
          <ImportPage myRole={myRole} themenfelder={themenfelder} allDatensaetze={allDatensaetze} />
        )}

        {step === STEP.ADMIN && (
          <AdminPortal onChatbotChange={setChatbotEnabled} />
        )}

        {deleteTarget && (
          <div className="modal-overlay" onClick={() => !deleteBusy && setDeleteTarget(null)}>
            <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
              <h2 style={{fontSize:"1.05rem",fontWeight:800,marginBottom:10}}>Statistik löschen?</h2>
              <p style={{fontSize:"0.88rem",color:"var(--ink)",fontWeight:600,marginBottom:6}}>
                {deleteTarget.datensatz}
              </p>
              <p style={{fontSize:"0.8rem",color:"var(--gray-700)",marginBottom:14}}>
                {deleteTarget.themenfeld} → {deleteTarget.indikator}
              </p>

              {deleteError && (
                <div className="admin-msg" style={{borderColor:"#ef4444",background:"rgba(239,68,68,0.08)",marginBottom:12}}>
                  {deleteError}
                </div>
              )}

              {!deleteInfo && !deleteError && (
                <p style={{fontSize:"0.82rem",color:"var(--gray-400)"}}>Prüfe…</p>
              )}

              {deleteInfo && (
                <div style={{background:"rgba(239,68,68,0.07)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:10,padding:"12px 14px",marginBottom:16}}>
                  {deleteInfo.values > 0 && (
                    <div style={{fontSize:"0.9rem",fontWeight:700,color:"#ef4444",marginBottom:4}}>
                      {formatNumber(deleteInfo.values, 0)} Messwerte werden gelöscht
                    </div>
                  )}
                  <div style={{fontSize:"0.8rem",color:"var(--gray-700)",lineHeight:1.5}}>
                    {deleteInfo.note}
                  </div>
                </div>
              )}

              <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                <button className="btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
                  Abbrechen
                </button>
                <button
                  className="btn-primary"
                  style={{background:"#ef4444",color:"white"}}
                  onClick={confirmDelete}
                  disabled={deleteBusy || !deleteInfo}>
                  {deleteBusy ? "Lösche…" : "Endgültig löschen"}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === STEP.CHAT && chatbotEnabled && (
          <ChatPage
            currentUser={currentUser}
            pendingQuestion={pendingChatQuestion}
            pendingChatId={pendingChatId}
            onPendingConsumed={() => { setPendingChatQuestion(null); setPendingChatId(null); }}
            onOpenStat={(stat, chatId) => { setPreviousStep(STEP.CHAT); setPendingChatId(chatId); selectStat(stat); setStep(STEP.RESULTS); }}
          />
        )}

      </div>

      {chatbotEnabled && (
        <ChatBot onNavigateToChat={(question) => {
          setStep(STEP.CHAT);
          // Frage wird via pendingChatQuestion an ChatPage weitergegeben
          setPendingChatQuestion(question);
        }} />
      )}

      </>)} {/* End auth conditional */}
    </div>
  );
}
